import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Building2, CreditCard, Mail, Upload, FileText, SettingsIcon, Receipt, Users, RepeatIcon, CheckIcon, Sparkles, Gift, GripVertical, Settings2 } from 'lucide-react';
import { DndContext, closestCenter, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { LazyEmailEditor as EmailEditor } from '@/components/LazyEmailEditor';
import { InvoiceEmailPreview } from '@/components/InvoiceEmailPreview';
import { defaultTemplates, previewData } from '@/lib/emailTemplates';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCompanySettings, updateCompanySettings, getCompany } from '@/lib/api';
import type { CompanyProfile, CompanySettings, InvoiceSettings, Invoice } from '@/types/firebase';
import { useAuth } from '@/components/AuthProvider';
import { useEffectiveCompanyId } from '@/hooks/useEffectiveCompanyId';
import { US_STATES, COUNTRIES } from '@/lib/constants';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { showNotification } from '@/lib/generalNotifications';
import { Switch } from '@/components/ui/switch';
import { InvoicePreview } from '@/components/InvoicePreview';
import { cn } from '@/lib/utils';
import { UserDialog } from '@/components/dialogs/UserDialog';
import { RoleDialog } from '@/components/dialogs/RoleDialog';
import { ConfirmDeleteDialog } from '@/components/dialogs/ConfirmDeleteDialog';
import { getUsers, getRoles, deleteRole, deleteUser, createRole, canDeleteUser } from '@/lib/api';
import { User, Role } from '@/types/firebase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { CardGlow } from '@/components/ui/CardGlow';
import { getBrandingConfig } from '@/utils/branding';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { isAchPaymentAvailable } from '@/utils/paymentGateways';
import { RichTextEditor } from '@/components/RichTextEditor';

interface CompanySettingsWithLogo extends CompanySettings {
  logo?: string;
  invoiceSettings: Required<InvoiceSettings>;
}

type EmailTemplateKey = 'paymentRequest' | 'paymentThankYou' | 'refundIssued' | 'paymentFailed' | 'invoice' | 'recurringInvoice' | 'donationThankYou' | 'userInvitation';
type EmailSubjectKey = 'paymentRequestSubject' | 'paymentThankYouSubject' | 'refundIssuedSubject' | 'paymentFailedSubject' | 'invoiceTemplateSubject' | 'recurringInvoiceSubject' | 'userInvitationSubject';

const defaultSettings: Required<InvoiceSettings> = {
  showLogo: false,
  enablePlatformFee: false,
  industry: 'none',
  invoiceType: 'standard',
  paymentMethods: {
    credit_card: true,
    paypal: false,
    ach: false,
    check: false
  },
  invoiceStartingNumber: 1000,
  enableDatePrefix: false,
  invoicePrefix: '',
  showPhoneOnInvoice: true,
  showEmailOnInvoice: true,
  showNotesOnInvoice: true,
  showTermsOnInvoice: true,
  invoicePhone: '',
  invoiceEmail: '',
  colors: {
    primary: '#4F46E5',
    secondary: '#6B7280',
    headerText: '#111827',
    tableHeader: '#F3F4F6',
    tableHeaderText: '#111827',
    totalText: '#111827',
    linkText: '#FFFFFF',
    buttonBackground: '#22C55E'
  },
  template: 'professional',
  numberFormat: 'INV-{NUMBER}',
  zeroPadding: 5,
  resetNumberSequence: 'never',
  dateFormat: 'YYYY-MM-DD',
  customFooterText: "Thank you for your business! Your satisfaction is our priority. If you have any questions or need assistance, please don't hesitate to reach out to our support team via email or phone. We're here to help and value your continued partnership.",
  defaultDueTerms: 'net_30',
  defaultNotes: `Terms and Conditions:

1. Payment: Full payment is due according to the terms specified on the invoice. We accept various payment methods for your convenience.

2. Service Quality: We are committed to providing high-quality services/products that meet your expectations. Please inspect all deliverables upon receipt.

3. Questions & Support: For any questions or concerns about this invoice, please contact our billing department within 5 business days of receipt.

4. Late Payments: Overdue accounts may be subject to a late payment fee and/or service interruption.

5. Refunds & Disputes: Any disputes must be reported within 30 days of the invoice date. Refund requests will be reviewed on a case-by-case basis.

6. Privacy & Confidentiality: All business transactions and communications are treated with strict confidentiality.

We value your business and are committed to providing excellent service. Thank you for choosing us as your trusted partner.`,
  showUnpaidWatermark: false,
  showPaidWatermark: false,
};

// Tab configuration
interface TabConfig {
  id: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
  isConditional?: boolean;
  condition?: (user: any) => boolean;
}

const defaultTabOrder: TabConfig[] = [
  {
    id: 'company',
    value: 'company',
    icon: Building2,
    label: 'Company',
    color: ''
  },
  {
    id: 'invoice',
    value: 'invoice',
    icon: Receipt,
    label: 'Invoice',
    color: ''
  },
  {
    id: 'payment',
    value: 'payment',
    icon: CreditCard,
    label: 'Payment',
    color: ''
  },
  {
    id: 'email',
    value: 'email',
    icon: Mail,
    label: 'Email',
    color: ''
  },
  {
    id: 'users',
    value: 'users',
    icon: Users,
    label: 'Users',
    color: '',
    isConditional: true,
    condition: (user) => user?.role === 'admin' || user?.role === 'super_admin'
  }
];

// Sortable Tab Trigger component
function SortableTabTrigger({ tab, isActive, onClick }: { 
  tab: TabConfig; 
  isActive: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = tab.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center group relative",
        isDragging && "z-50"
      )}
      {...attributes}
    >
      {/* Drag Handle */}
      <div
        {...listeners}
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded mr-1"
      >
        <GripVertical className="w-3 h-3 text-gray-400" />
      </div>
      
      {/* Tab Trigger */}
      <TabsTrigger
        value={tab.value}
        onClick={onClick}
        className="min-w-0"
      >
        <IconComponent className="w-4 h-4 mr-2" />
        {tab.label}
      </TabsTrigger>
    </div>
  );
}

// Add at the beginning of the file before the Settings component, around line 90
function useIsReadyToDelete(userId: string) {
  const [canDelete, setCanDelete] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if the user can be deleted
  const checkCanDelete = useCallback(async () => {
    setIsChecking(true);
    setError(null);
    try {
      const result = await canDeleteUser(userId);
      setCanDelete(result.canDelete);
      if (!result.canDelete) {
        setError(result.reason || 'This user cannot be deleted');
      }
    } catch (error) {
      console.error('Error checking if user can be deleted:', error);
      setCanDelete(false);
      setError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsChecking(false);
    }
  }, [userId]);

  // Check when component mounts
  useEffect(() => {
    checkCanDelete();
  }, [checkCanDelete]);

  return { canDelete, isChecking, error, checkCanDelete };
}

export default function Settings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTabValue = searchParams.get('tab') || 'company';
  const { user } = useAuth();
  const { effectiveCompanyId } = useEffectiveCompanyId();
  const companyId = effectiveCompanyId || '';
  const brandingConfig = getBrandingConfig();
  const appDisplayName = brandingConfig.isInvoicerGG ? 'Invoicer.gg' : 'InvoiceBug';
  const [tabValue, setTabValue] = useState<string>(initialTabValue);
  const [showSavedState, setShowSavedState] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [previewMode, setPreviewMode] = useState<boolean>(false);

  // Tab order state management
  const [tabOrder, setTabOrder] = useState<TabConfig[]>(() => {
    const savedOrder = localStorage.getItem('settings-tab-order');
    if (savedOrder) {
      try {
        const parsedOrder = JSON.parse(savedOrder);
        // Merge saved order with default tabs to ensure new tabs are included
        const mergedOrder = [...defaultTabOrder];
        parsedOrder.forEach((savedTab: any, index: number) => {
          const defaultTabIndex = mergedOrder.findIndex(tab => tab.id === savedTab.id);
          if (defaultTabIndex !== -1) {
            // Move the tab to the saved position
            const [tab] = mergedOrder.splice(defaultTabIndex, 1);
            mergedOrder.splice(index, 0, tab);
          }
        });
        return mergedOrder;
      } catch {
        return defaultTabOrder;
      }
    }
    return defaultTabOrder;
  });

  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState<boolean>(false);
  const [formData, setFormData] = useState<CompanySettingsWithLogo>({
    id: '',
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zip: ''
    },
    email: '',
    phone: '',
    taxId: '',
    logo: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    platformFee: 0,
    defaultTaxRate: 0,
    emailSettings: {
      sendgridApiKey: '',
      fromEmail: '',
      invoiceTemplate: defaultTemplates.invoiceTemplate,
      invoiceTemplateSubject: defaultTemplates.invoiceTemplateSubject,
      paymentRequest: defaultTemplates.paymentRequest,
      paymentThankYou: defaultTemplates.paymentThankYou,
      refundIssued: defaultTemplates.refundIssued,
      paymentFailed: defaultTemplates.paymentFailed,
      paymentRequestSubject: defaultTemplates.paymentRequestSubject,
      paymentThankYouSubject: defaultTemplates.paymentThankYouSubject,
      refundIssuedSubject: defaultTemplates.refundIssuedSubject,
      paymentFailedSubject: defaultTemplates.paymentFailedSubject,
      recurringInvoice: defaultTemplates.recurringInvoice,
      recurringInvoiceSubject: defaultTemplates.recurringInvoiceSubject,
      enableThankYouEmail: false,
      enableRefundEmail: true, // Default to enabled for new users
      enableFailedPaymentEmail: false
    },
    invoiceSettings: defaultSettings
  });
  
  // State for handling file uploads
  const [dragging, setDragging] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State for handling virtual terminal settings
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [passwordAttempt, setPasswordAttempt] = useState('');
  const [selectedField, setSelectedField] = useState<'tokenization' | 'security' | null>(null);
  const [editableFields, setEditableFields] = useState<{
    tokenization: boolean;
    security: boolean;
  }>({
    tokenization: false,
    security: false
  });
  
  // For security, define these mutating functions that are actually used in the code
  const setIsSavingTemplate = (_value: boolean) => {
    // This is a no-op function since we don't actually track this state
    // but it's needed for the code to compile
  };

  const setIsSaving = (_value: boolean) => {
    // This is a no-op function since we don't actually track this state
    // but it's needed for the code to compile
  };

  // Fetch company settings (customer-level)
  const { data: settings, isLoading: isSettingsLoading } = useQuery({
    queryKey: ['companySettings', companyId],
    queryFn: () => getCompanySettings(companyId),
    enabled: !!companyId
  });
  // Fetch tenant-level company data (admin-level)
  const { data: tenantCompany, isLoading: isTenantLoading } = useQuery({
    queryKey: ['tenantCompany', companyId],
    queryFn: () => getCompany(companyId),
    enabled: !!companyId
  });

  // Check if ACH is enabled at the gateway level
  // Use settings (the company's own settings) to check ACH availability
  const isAchGatewayEnabled = useMemo(() => isAchPaymentAvailable(settings), [settings]);

  // Effect to disable ACH in form if gateway ACH is disabled
  useEffect(() => {
    if (!isAchGatewayEnabled && formData.invoiceSettings?.paymentMethods?.ach) {
      setFormData(prev => ({
        ...prev,
        invoiceSettings: {
          ...defaultSettings,
          ...(prev.invoiceSettings || {}),
          paymentMethods: {
            ...(prev.invoiceSettings?.paymentMethods || defaultSettings.paymentMethods),
            ach: false
          }
        }
      }));
      setHasUnsavedChanges(true);
    }
  }, [isAchGatewayEnabled, formData.invoiceSettings?.paymentMethods?.ach]);

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (updates: Partial<CompanyProfile>) =>
      updateCompanySettings(companyId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companySettings'] });
      setShowSavedState(true);
      setHasUnsavedChanges(false);
      // Reset the saved state when new changes are made
      const timer = setTimeout(() => {
        if (!hasUnsavedChanges) {
          setShowSavedState(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  });

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveTabId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTabId(null);

    if (active.id !== over?.id) {
      setTabOrder((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newItems = [...items];
          const [movedItem] = newItems.splice(oldIndex, 1);
          newItems.splice(newIndex, 0, movedItem);

          // Save to localStorage
          localStorage.setItem('settings-tab-order', JSON.stringify(newItems));
          
          return newItems;
        }
        return items;
      });
    }
  };

  // Get visible tabs (filtered by conditions)
  const visibleTabs = tabOrder.filter(tab => 
    !tab.isConditional || (tab.condition && tab.condition(user))
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (settings) {
      const formattedSettings: Partial<CompanySettings> = {
        ...settings,
        address: {
          ...(typeof settings.address === 'string' 
            ? { street: settings.address }
            : settings.address || {}),
          country: settings.address?.country || 'US'
        }
      };
      setFormData(formattedSettings as CompanySettingsWithLogo);
      setHasUnsavedChanges(false);
      setShowSavedState(false);
    }
  }, [settings]);

  const handleNestedUpdate = (parent: keyof CompanySettingsWithLogo, child: string, value: unknown) => {
    setHasUnsavedChanges(true);
    setShowSavedState(false);
    if (parent === 'invoiceSettings' && child === 'colors') {
      setFormData((prev: CompanySettingsWithLogo) => {
        const currentSettings = {
          ...defaultSettings,
          ...(prev.invoiceSettings || {})
        };
        
        // Get the color key from the input name
        const colorKey = (value as { name: string }).name?.split('.')[2];
        const colorValue = (value as { value: string }).value;
        
        
        if (colorKey) {
          const newState = {
            ...prev,
            invoiceSettings: {
              ...currentSettings,
              colors: {
                ...currentSettings.colors,
                [colorKey]: colorValue
              }
            }
          };
          
          // Auto-save the color change
          setTimeout(() => {
            updateSettingsMutation.mutate(newState);
          }, 0);
          
          return newState;
        }
        return prev;
      });
    } else if (parent === 'invoiceSettings') {
      setFormData((prev: CompanySettingsWithLogo) => {
        const currentSettings = {
          ...defaultSettings,
          ...(prev.invoiceSettings || {})
        };

        return {
          ...prev,
          invoiceSettings: {
            ...currentSettings,
            invoiceType: currentSettings.invoiceType || 'standard',
            [child]: value
          }
        };
      });
    } else if (parent === 'emailSettings') {
      setFormData((prev: CompanySettingsWithLogo) => {
        const currentEmailSettings = prev.emailSettings || {
          sendgridApiKey: '',
          fromEmail: '',
          invoiceTemplate: '',
          paymentRequest: '',
          paymentThankYou: '',
          refundIssued: '',
          paymentFailed: '',
          paymentRequestSubject: '',
          paymentThankYouSubject: '',
          refundIssuedSubject: '',
          paymentFailedSubject: '',
          invoiceTemplateSubject: '',
          recurringInvoice: '',
          recurringInvoiceSubject: ''
        };
        return {
          ...prev,
          emailSettings: {
            ...currentEmailSettings,
            [child]: value
          }
        };
      });
    } else {
      setFormData((prev: CompanySettingsWithLogo) => {
        const parentObj = prev[parent] as Record<string, unknown>;
        return {
          ...prev,
          [parent]: {
            ...(parentObj || {}),
            [child]: value
          }
        };
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setHasUnsavedChanges(true);
    setShowSavedState(false);
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (parent === 'invoiceSettings' && child === 'colors') {
        handleNestedUpdate('invoiceSettings', 'colors', { name, value });
      } else {
        handleNestedUpdate(parent as keyof CompanySettingsWithLogo, child, value);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUnsavedChanges(true);
    setShowSavedState(false);
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);
    
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
      }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragging(true);
    } else if (e.type === "dragleave") {
      setDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      return;
    }

    try {
      setFileUploadLoading(true);
      const storage = getStorage();
      const fileExtension = file.name.split('.').pop();
      const fileName = `logo_${Date.now()}.${fileExtension}`;

      // Ensure company ID is in the correct format for storage path
      const storageCompanyId = companyId?.startsWith('company_')
        ? companyId
        : `company_${companyId}`;

      const storageRef = ref(storage, `companies/${storageCompanyId}/logo/${fileName}`);

      // Upload file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update company settings with new logo URL
      await updateSettingsMutation.mutateAsync({
        ...formData,
        logo: downloadURL
      });

      setFormData(prev => ({
        ...prev,
        logo: downloadURL
      }));
    } catch (error) {
      console.error('Logo upload error:', error);
    } finally {
      setFileUploadLoading(false);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleEmailTemplateChange = (templateType: EmailTemplateKey, content: string) => {
    setFormData((prevState) => {
      const emailSettings = {
        sendgridApiKey: prevState.emailSettings?.sendgridApiKey ?? '',
        fromEmail: prevState.emailSettings?.fromEmail ?? '',
        invoiceTemplate: prevState.emailSettings?.invoiceTemplate ?? '',
        invoiceTemplateSubject: prevState.emailSettings?.invoiceTemplateSubject ?? defaultTemplates.invoiceTemplateSubject,
        paymentRequest: prevState.emailSettings?.paymentRequest ?? defaultTemplates.paymentRequest,
        paymentThankYou: prevState.emailSettings?.paymentThankYou ?? defaultTemplates.paymentThankYou,
        refundIssued: prevState.emailSettings?.refundIssued ?? defaultTemplates.refundIssued,
        paymentFailed: prevState.emailSettings?.paymentFailed ?? defaultTemplates.paymentFailed,
        paymentRequestSubject: prevState.emailSettings?.paymentRequestSubject ?? defaultTemplates.paymentRequestSubject,
        paymentThankYouSubject: prevState.emailSettings?.paymentThankYouSubject ?? defaultTemplates.paymentThankYouSubject,
        refundIssuedSubject: prevState.emailSettings?.refundIssuedSubject ?? defaultTemplates.refundIssuedSubject,
        paymentFailedSubject: prevState.emailSettings?.paymentFailedSubject ?? defaultTemplates.paymentFailedSubject,
        recurringInvoice: prevState.emailSettings?.recurringInvoice ?? defaultTemplates.recurringInvoice,
        recurringInvoiceSubject: prevState.emailSettings?.recurringInvoiceSubject ?? defaultTemplates.recurringInvoiceSubject,
        donationThankYou: prevState.emailSettings?.donationThankYou ?? defaultTemplates.donationThankYou,
        donationThankYouSubject: prevState.emailSettings?.donationThankYouSubject ?? defaultTemplates.donationThankYouSubject,
        enableThankYouEmail: prevState.emailSettings?.enableThankYouEmail ?? false,
        enableRefundEmail: prevState.emailSettings?.enableRefundEmail ?? true,
        enableDonationThankYouEmail: prevState.emailSettings?.enableDonationThankYouEmail ?? false,
        enableFailedPaymentEmail: prevState.emailSettings?.enableFailedPaymentEmail ?? false
      };

      // Handle the template content update
      const updatedSettings = {
          ...emailSettings,
          [templateType === 'invoice' ? 'invoiceTemplate' : templateType]: content,
      };

      return {
        ...prevState,
        emailSettings: updatedSettings
      };
    });
  };

  const handleEmailSubjectChange = (templateType: EmailTemplateKey, subject: string) => {
    setFormData((prevState) => {
      const emailSettings = {
        // Preserve all existing settings
        ...prevState.emailSettings,
        // Update the specific subject
        [`${templateType}Subject`]: subject
      };

      return {
        ...prevState,
        emailSettings
      };
    });

    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
  };

  const handleSelectChange = (field: string) => (value: string) => {
    setHasUnsavedChanges(true);
    setShowSavedState(false);
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (parent === 'address') {
        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            [child]: value,
          },
        }));
      } else if (parent === 'invoiceSettings') {
        setFormData((prev: CompanySettingsWithLogo): CompanySettingsWithLogo => {
          const currentSettings: InvoiceSettings = {
            ...defaultSettings,
            ...(prev.invoiceSettings || {}),
          };
          return {
            ...prev,
            invoiceSettings: {
              ...currentSettings,
              invoiceType: currentSettings.invoiceType || 'standard',
              [child]: value,
            },
          };
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSwitchChange = (field: keyof InvoiceSettings) => (checked: boolean) => {
    setHasUnsavedChanges(true);
    setShowSavedState(false);
    setFormData((prevState: CompanySettingsWithLogo): CompanySettingsWithLogo => {
      const currentSettings: InvoiceSettings = {
        ...defaultSettings,
        ...(prevState.invoiceSettings || {}),
      };
      return {
        ...prevState,
        invoiceSettings: {
          ...currentSettings,
          [field]: checked,
        }
      };
    });
  };

  const handleSaveTemplate = async (templateType: EmailTemplateKey) => {
  try {
    setIsSavingTemplate(true);

    // Create a complete emailSettings object that preserves all existing templates and settings
    const emailSettings = {
      // Base settings - use form data values if provided, otherwise preserve existing values
      fromEmail: formData.emailSettings?.fromEmail || settings?.emailSettings?.fromEmail || '',
      enableThankYouEmail: formData.emailSettings?.enableThankYouEmail || false,
      enableRefundEmail: formData.emailSettings?.enableRefundEmail ?? true,
      enableDonationThankYouEmail: formData.emailSettings?.enableDonationThankYouEmail || false,
      enableFailedPaymentEmail: formData.emailSettings?.enableFailedPaymentEmail || false,
      
      // Preserve all existing templates and subjects
      ...formData.emailSettings,
      
      // Ensure we have default values for required fields
      invoiceTemplate: formData.emailSettings?.invoiceTemplate || defaultTemplates.invoiceTemplate,
      invoiceTemplateSubject: formData.emailSettings?.invoiceTemplateSubject || defaultTemplates.invoiceTemplateSubject,
      paymentRequest: formData.emailSettings?.paymentRequest || defaultTemplates.paymentRequest,
      paymentRequestSubject: formData.emailSettings?.paymentRequestSubject || defaultTemplates.paymentRequestSubject,
      paymentThankYou: formData.emailSettings?.paymentThankYou || defaultTemplates.paymentThankYou,
      paymentThankYouSubject: formData.emailSettings?.paymentThankYouSubject || defaultTemplates.paymentThankYouSubject,
      refundIssued: formData.emailSettings?.refundIssued || defaultTemplates.refundIssued,
      refundIssuedSubject: formData.emailSettings?.refundIssuedSubject || defaultTemplates.refundIssuedSubject,
      paymentFailed: formData.emailSettings?.paymentFailed || defaultTemplates.paymentFailed,
      paymentFailedSubject: formData.emailSettings?.paymentFailedSubject || defaultTemplates.paymentFailedSubject,
      recurringInvoice: formData.emailSettings?.recurringInvoice || defaultTemplates.recurringInvoice,
      recurringInvoiceSubject: formData.emailSettings?.recurringInvoiceSubject || defaultTemplates.recurringInvoiceSubject,
      donationThankYou: formData.emailSettings?.donationThankYou || defaultTemplates.donationThankYou,
      donationThankYouSubject: formData.emailSettings?.donationThankYouSubject || defaultTemplates.donationThankYouSubject,
    };
      
      // Update the specific template being edited
      if (templateType === 'invoice') {
        emailSettings.invoiceTemplate = formData.emailSettings?.invoiceTemplate || defaultTemplates.invoiceTemplate;
        emailSettings.invoiceTemplateSubject = formData.emailSettings?.invoiceTemplateSubject || defaultTemplates.invoiceTemplateSubject;
      } else if (templateType === 'donationThankYou') {
        emailSettings.donationThankYou = formData.emailSettings?.donationThankYou || defaultTemplates.donationThankYou;
        emailSettings.donationThankYouSubject = formData.emailSettings?.donationThankYouSubject || defaultTemplates.donationThankYouSubject;
      } else {
        emailSettings[templateType] = formData.emailSettings?.[templateType] || defaultTemplates[templateType];
        emailSettings[`${templateType}Subject`] = formData.emailSettings?.[`${templateType}Subject`] || defaultTemplates[`${templateType}Subject`];
      }
      
      // Update with proper type casting
      await updateSettingsMutation.mutateAsync({
        emailSettings
      } as Partial<CompanySettings>);
      
      showNotification({
        title: "Settings Saved",
        message: "Email settings have been updated successfully",
        type: "success"
      });
    } catch (error) {
      showNotification({
        title: "Save Failed",
        message: "Failed to save email settings. Please try again.",
        type: "error"
      });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    try {
      setIsSaving(true);
      
      // Prepare email settings update - preserve all existing settings
      const emailSettings = {
        // Base settings - use form data values if provided, otherwise preserve existing values
        fromEmail: formData.emailSettings?.fromEmail || settings?.emailSettings?.fromEmail || '',
        enableThankYouEmail: formData.emailSettings?.enableThankYouEmail || false,
        enableRefundEmail: formData.emailSettings?.enableRefundEmail ?? true,
        enableDonationThankYouEmail: formData.emailSettings?.enableDonationThankYouEmail || false,
        enableFailedPaymentEmail: formData.emailSettings?.enableFailedPaymentEmail || false,
        
        // All templates and subjects
        invoiceTemplate: formData.emailSettings?.invoiceTemplate || defaultTemplates.invoiceTemplate,
        invoiceTemplateSubject: formData.emailSettings?.invoiceTemplateSubject || defaultTemplates.invoiceTemplateSubject,
        paymentRequest: formData.emailSettings?.paymentRequest || defaultTemplates.paymentRequest,
        paymentRequestSubject: formData.emailSettings?.paymentRequestSubject || defaultTemplates.paymentRequestSubject,
        paymentThankYou: formData.emailSettings?.paymentThankYou || defaultTemplates.paymentThankYou,
        paymentThankYouSubject: formData.emailSettings?.paymentThankYouSubject || defaultTemplates.paymentThankYouSubject,
        refundIssued: formData.emailSettings?.refundIssued || defaultTemplates.refundIssued,
        refundIssuedSubject: formData.emailSettings?.refundIssuedSubject || defaultTemplates.refundIssuedSubject,
        paymentFailed: formData.emailSettings?.paymentFailed || defaultTemplates.paymentFailed,
        paymentFailedSubject: formData.emailSettings?.paymentFailedSubject || defaultTemplates.paymentFailedSubject,
        recurringInvoice: formData.emailSettings?.recurringInvoice || defaultTemplates.recurringInvoice,
        recurringInvoiceSubject: formData.emailSettings?.recurringInvoiceSubject || defaultTemplates.recurringInvoiceSubject,
        donationThankYou: formData.emailSettings?.donationThankYou || defaultTemplates.donationThankYou,
        donationThankYouSubject: formData.emailSettings?.donationThankYouSubject || defaultTemplates.donationThankYouSubject,
        
        // Additional templates if they exist
        userInvitation: formData.emailSettings?.userInvitation || defaultTemplates.userInvitation,
        userInvitationSubject: formData.emailSettings?.userInvitationSubject || defaultTemplates.userInvitationSubject,
      };
      
      
      // Update company settings
      await updateSettingsMutation.mutateAsync({
        emailSettings
      });
      
      setHasUnsavedChanges(false);
      setShowSavedState(true);
      showNotification({
        title: "Email Settings Saved",
        message: "Your email settings have been updated successfully",
        type: "success"
      });
      
      // Reset editable fields after saving
      setEditableFields({
        tokenization: false,
        security: false
      });
    } catch (error) {
      console.error('Failed to save email settings:', error);
      showNotification({
        title: "Save Failed",
        message: "Failed to save email settings. Please try again.",
        type: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add sample invoice data for preview
  const sampleInvoice: Partial<Invoice> = {
    // Note: invoiceNumber is intentionally omitted so InvoicePreview generates it from settings
    customerName: "Sample Customer",
    customerEmail: "customer@example.com",
    customerAddress: "123 Main St, City, State 12345",
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [
      {
        id: "1",
        categoryId: "electronics",
        categoryName: "Electronics",
        productId: "laptop",
        productName: "Laptop Computer",
        description: "High-performance laptop with 16GB RAM and 512GB SSD",
        quantity: 2,
        unitPrice: 100,
        total: 200,
        isTaxable: true
      },
      {
        id: "2",
        categoryId: "services",
        categoryName: "Services",
        productId: "support",
        productName: "Technical Support",
        description: "Premium technical support and maintenance package",
        quantity: 1,
        unitPrice: 150,
        total: 150,
        isTaxable: false
      }
    ],
    subtotal: 350,
    taxRate: formData.defaultTaxRate || 0,
    taxAmount: 350 * ((formData.defaultTaxRate || 0) / 100),
    total: 350 * (1 + (formData.defaultTaxRate || 0) / 100),
    status: 'draft' as const,
    notes: formData.invoiceSettings?.defaultNotes || ''
  };

  const handleResetColors = () => {
    setFormData((prev: CompanySettingsWithLogo) => {
      const currentSettings = {
        ...prev.invoiceSettings,
        colors: {
          primary: '#4F46E5',
          secondary: '#6B7280',
          headerText: '#111827',
          tableHeader: '#F3F4F6',
          tableHeaderText: '#111827',
          totalText: '#111827',
          linkText: '#FFFFFF',
          buttonBackground: '#22C55E'
        }
      };

      return {
        ...prev,
        invoiceSettings: currentSettings
      };
    });
  };

  const handlePaymentMethodChange = (method: keyof Required<InvoiceSettings>['paymentMethods'], checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      invoiceSettings: {
        ...prev.invoiceSettings,
        paymentMethods: {
          ...prev.invoiceSettings?.paymentMethods,
          [method]: checked,
          // Always ensure credit_card is true regardless of other selections
          credit_card: true
        }
      }
    }));
  };

  // Function to handle password verification
  const handlePasswordVerify = () => {
    if (passwordAttempt === 'Sky') {
      setShowVerificationDialog(false);
      setPasswordAttempt('');
      
      // Mark the field as editable
      if (selectedField) {
        setEditableFields(prev => ({
          ...prev,
          [selectedField]: true
        }));
        
        // Focus the field after unlock
        setTimeout(() => {
          const input = document.getElementById(
            selectedField === 'tokenization' ? 'wikiPayitTokenizationKey' : 'wikiPayitSecurityKey'
          );
          if (input) {
            input.focus();
          }
        }, 100);
      }
    } else {
      showNotification({
        title: "Invalid Password",
        message: "Please enter the correct password to modify payment credentials.",
        type: "error"
      });
    }
  };

  // Function to handle credential field click
  const handleCredentialFieldClick = (field: 'tokenization' | 'security') => {
    setSelectedField(field);
    setShowVerificationDialog(true);
  };

  // Only allow admin access
  if (!user) {
    return (
      <div className="max-w-6xl mx-auto pb-6">
        <p className="text-gray-500 mb-6">Loading settings...</p>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
          <div className="h-[400px] bg-gray-200 rounded-lg w-full"></div>
        </div>
      </div>
    );
  }

  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-slate-900">Access Denied</h2>
          <p className="text-gray-500 mt-1">You don't have permission to access settings.</p>
        </div>
      </div>
    );
  }

  if (isSettingsLoading) {
    return (
      <div className="max-w-6xl mx-auto pb-6">
        <p className="text-gray-500 mb-6">Loading settings...</p>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
          <div className="h-[400px] bg-gray-200 rounded-lg w-full"></div>
        </div>
      </div>
    );
  }

  // Calculate profile completion percentage
  const calculateProfileCompletion = (): number => {
    let completedFields = 0;
    let totalFields = 0;
    
    // Company info fields
    const companyFields = ['name', 'email', 'phone', 'taxId'];
    companyFields.forEach(field => {
      totalFields++;
      if (formData[field as keyof typeof formData]) completedFields++;
    });
    
    // Address fields
    const addressFields = ['street', 'city', 'state', 'zip', 'country'];
    addressFields.forEach(field => {
      totalFields++;
      if (formData.address && formData.address[field as keyof typeof formData.address]) completedFields++;
    });
    
    // Logo
    totalFields++;
    if (formData.logo) completedFields++;
    
    // Payment settings
    totalFields += 2;
    if (formData.wikiPayitSettings?.username) completedFields++;
    if (formData.wikiPayitSettings?.tokenizationKey) completedFields++;
    
    // Email settings
    totalFields++;
    if (formData.emailSettings?.fromEmail) completedFields++;
    
    return Math.round((completedFields / totalFields) * 100);
  };
  
  // Get message based on completion percentage
  const getProfileCompletionMessage = (): string => {
    const percentage = calculateProfileCompletion();
    
    if (percentage < 30) {
      return "Let's get started! Complete your basic company information.";
    } else if (percentage < 60) {
      return "Good progress! Add your payment settings to accept payments.";
    } else if (percentage < 90) {
      return "Almost there! Customize your email templates for a professional touch.";
    } else {
      return "Excellent! Your profile is complete and ready for business.";
    }
  };

  return (
    <div className="space-y-6 relative pb-24">

      {/* Profile Completion Indicator */}
      <div className="bg-white/80 backdrop-blur-sm border rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium">Profile Completion</h3>
          <span className="text-xs text-muted-foreground">
            {calculateProfileCompletion()}% Complete
          </span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[hsl(var(--villavie-green))] to-[hsl(var(--villavie-blue))]"
            style={{ width: `${calculateProfileCompletion()}%` }}
          />
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {getProfileCompletionMessage()}
        </div>
      </div>

      {/* Floating Save Button */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
          <Button
            onClick={async () => {
              try {
                const update: Partial<CompanyProfile> = {
                  name: formData.name,
                  address: formData.address,
                  phone: formData.phone,
                  email: formData.email,
                  taxId: formData.taxId,
                  platformFee: formData.platformFee,
                  defaultTaxRate: formData.defaultTaxRate,
                  wikiPayitSettings: formData.wikiPayitSettings,
                  enableRecurringInvoices: formData.enableRecurringInvoices,
                  enableInstallmentInvoices: formData.enableInstallmentInvoices,
                  emailSettings: formData.emailSettings ? {
                    ...formData.emailSettings,
                    enableDonationThankYouEmail: formData.emailSettings.enableDonationThankYouEmail || false,
                    donationThankYou: formData.emailSettings.donationThankYou || defaultTemplates.donationThankYou,
                    donationThankYouSubject: formData.emailSettings.donationThankYouSubject || defaultTemplates.donationThankYouSubject,
                    enableFailedPaymentEmail: formData.emailSettings.enableFailedPaymentEmail || false
                  } : undefined
                };
                await updateSettingsMutation.mutateAsync(update);
                
                // Handle invoice settings separately
                if (formData.invoiceSettings) {
                  await updateCompanySettings(companyId, {
                    invoiceSettings: formData.invoiceSettings
                  } as any);
                }
                
                showNotification({
                  title: "Settings Saved",
                  message: "Your settings have been updated successfully",
                  type: "success"
                });
                
                setHasUnsavedChanges(false);
                setShowSavedState(true);
                
                // Reset after 2 seconds
                setTimeout(() => {
                  setShowSavedState(false);
                }, 2000);
              } catch (error) {
                showNotification({
                  title: "Save Failed",
                  message: "Failed to save settings. Please try again.",
                  type: "error"
                });
              }
            }}
            disabled={updateSettingsMutation.isPending}
            className="shadow-lg hover:shadow-xl transition-all px-6 py-6 rounded-full bg-primary hover:bg-primary hover:brightness-110"
            size="lg"
          >
            {updateSettingsMutation.isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-r-foreground" />
                <span>Saving...</span>
              </div>
            ) : (
              <span className="flex items-center gap-2">
                <CheckIcon className="h-5 w-5" />
                Save All Changes
              </span>
            )}
          </Button>
        </div>
      )}

      <div className="relative h-full">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-white to-[hsl(var(--villavie-warm-gray))] opacity-50 pointer-events-none rounded-lg"></div>
        
        <Tabs value={tabValue} onValueChange={setTabValue} className="space-y-6 relative z-10">
          <div className="sticky top-0 z-20 pt-2 pb-2 bg-white/95 backdrop-blur-sm border-b mb-6">
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <TabsList className="w-full">
                <SortableContext items={visibleTabs.map(tab => tab.id)} strategy={horizontalListSortingStrategy}>
                  {visibleTabs.map((tab) => (
                    <SortableTabTrigger
                      key={tab.id}
                      tab={tab}
                      isActive={tabValue === tab.value}
                      onClick={() => setTabValue(tab.value)}
                    />
                  ))}
                </SortableContext>
              </TabsList>
              <DragOverlay>
                {activeTabId ? (
                  <div className="flex items-center bg-white/90 backdrop-blur-sm border rounded px-3 py-2 shadow-lg">
                    {(() => {
                      const activeTab = visibleTabs.find(tab => tab.id === activeTabId);
                      if (!activeTab) return null;
                      const IconComponent = activeTab.icon;
                      return (
                        <>
                          <IconComponent className="w-4 h-4 mr-2" />
                          {activeTab.label}
                        </>
                      );
                    })()}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>

          <div className="overflow-visible pb-32">
            {/* Company Tab */}
            <TabsContent value="company">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold text-primary">Company Profile</h2>
                    <p className="text-sm text-muted-foreground">Manage your organization information</p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const update: Partial<CompanyProfile> = {
                          name: formData.name,
                          address: formData.address,
                          phone: formData.phone,
                          email: formData.email,
                          taxId: formData.taxId
                        };
                        await updateSettingsMutation.mutateAsync(update);
                        showNotification({
                          title: "Settings Saved",
                          message: "Company information has been updated successfully",
                          type: "success"
                        });
                      } catch (error) {
                        showNotification({
                          title: "Save Failed",
                          message: "Failed to save company information. Please try again.",
                          type: "error"
                        });
                      }
                    }}
                    disabled={updateSettingsMutation.isPending || (!hasUnsavedChanges && showSavedState)}
                    className={cn(
                      "shadow-sm transition-all hover:shadow-md",
                      showSavedState && !hasUnsavedChanges ? "bg-green-500 hover:bg-green-600" : "",
                      "px-4 gap-2"
                    )}
                  >
                    {updateSettingsMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-r-foreground" />
                        <span>Saving...</span>
                      </div>
                    ) : showSavedState && !hasUnsavedChanges ? (
                      <span className="flex items-center gap-2">
                        <CheckIcon className="h-4 w-4" />
                        Saved
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Save Changes
                      </span>
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
                  {/* Left Column - Logo and Industry */}
                  <div className="space-y-6">
                    {/* Logo Section */}
                    <CardGlow color="hsl(var(--villavie-green))" className="border shadow-sm">
                      <div className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">Company Logo</Label>
                          {formData.logo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive/90"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  logo: undefined
                                }));
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        <div
                          className={cn(
                            "relative group border-2 rounded-lg transition-all duration-200 ease-in-out",
                            dragging ? 'border-primary border-dashed bg-primary/5' : 'border-dashed border-gray-300 hover:border-primary/50',
                            formData.logo ? 'h-[220px]' : 'h-[200px]'
                          )}
                          onDragEnter={handleDrag}
                          onDragLeave={handleDrag}
                          onDragOver={handleDrag}
                          onDrop={handleDrop}
                        >
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleLogoChange}
                            accept="image/*"
                            className="hidden"
                          />
                          
                          {formData.logo ? (
                            <>
                              <div className="absolute inset-0 flex items-center justify-center p-6">
                                <img
                                  src={formData.logo}
                                  alt="Company logo"
                                  className="max-h-full max-w-full object-contain rounded-md"
                                />
                              </div>
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={triggerFileInput}
                                  disabled={fileUploadLoading}
                                  className="bg-white/95 hover:bg-white text-gray-900"
                                >
                                  {fileUploadLoading ? 'Uploading...' : 'Change Logo'}
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div 
                              className="h-full flex flex-col items-center justify-center text-center cursor-pointer hover:bg-gray-50 rounded-lg transition-colors p-6"
                              onClick={triggerFileInput}
                            >
                              <Upload className="h-12 w-12 text-gray-400 mb-3" />
                              <p className="text-sm text-gray-700 font-medium">
                                {fileUploadLoading ? 'Uploading...' : 'Drop your logo here or click to upload'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1.5">
                                PNG, JPG up to 5MB
                              </p>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Recommended: Square image, at least 200x200px
                        </p>
                      </div>
                    </CardGlow>

                    {/* Industry Settings */}
                    <CardGlow color="hsl(var(--villavie-warm-gray))" className="border shadow-sm">
                      <div className="p-6 space-y-4">
                        <div>
                          <Label className="text-base font-semibold">Industry Settings</Label>
                          <p className="text-sm text-muted-foreground">Select your business sector</p>
                        </div>
                        <Select
                          value={formData.invoiceSettings?.industry || 'none'}
                          onValueChange={(value) => handleSelectChange('invoiceSettings.industry')(value)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select Industry" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="cruise_ship">Cruise Ship</SelectItem>
                            <SelectItem value="retail">Retail</SelectItem>
                            <SelectItem value="hospitality">Hospitality</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="technology">Technology</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardGlow>
                  </div>

                  {/* Right Column - Company Details */}
                  <div className="space-y-6">
                    {/* Basic Info Section */}
                    <CardGlow color="hsl(var(--villavie-green))" className="border shadow-sm">
                      <div className="p-6 space-y-6">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-lg font-semibold">Basic Information</h3>
                            <div className="bg-green-50 text-green-700 text-xs rounded-full px-2 py-0.5 border border-green-200">Required</div>
                          </div>
                          <p className="text-sm text-muted-foreground">Your primary business details</p>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="name">Company Name</Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name || ''}
                              onChange={handleInputChange}
                              placeholder="Enter company name"
                              className="mt-1.5"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="email">Business Email</Label>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email || ''}
                                onChange={handleInputChange}
                                placeholder="company@example.com"
                                className="mt-1.5"
                              />
                            </div>
                            <div>
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input
                                id="phone"
                                name="phone"
                                value={formData.phone || ''}
                                onChange={handleInputChange}
                                placeholder="+1 (555) 000-0000"
                                className="mt-1.5"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="taxId">Tax ID / EIN</Label>
                            <Input
                              id="taxId"
                              name="taxId"
                              value={formData.taxId || ''}
                              onChange={handleInputChange}
                              placeholder="Enter tax ID"
                              className="mt-1.5"
                            />
                          </div>
                        </div>
                      </div>
                    </CardGlow>

                    {/* Address Section */}
                    <CardGlow color="hsl(var(--villavie-blue))" className="border shadow-sm">
                      <div className="p-6 space-y-6">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-lg font-semibold">Business Address</h3>
                            <div 
                              className={cn(
                                "text-xs rounded-full px-2 py-0.5 border",
                                "bg-blue-50 text-blue-700 border-blue-200"
                              )}
                            >
                              For Invoices
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">Your company's physical location</p>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <AddressAutocomplete
                              label="Street Address"
                              placeholder="Start typing your address..."
                              value={{
                                street: formData.address?.street || '',
                                city: formData.address?.city || '',
                                state: formData.address?.state || '',
                                zipCode: formData.address?.zip || '',
                                fullAddress: formData.address?.street
                                  ? `${formData.address.street}${formData.address.city ? ', ' + formData.address.city : ''}${formData.address.state ? ', ' + formData.address.state : ''}${formData.address.zip ? ' ' + formData.address.zip : ''}`
                                  : ''
                              }}
                              onChange={(address) => {
                                setHasUnsavedChanges(true);
                                setShowSavedState(false);
                                setFormData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    street: address.street || '',
                                    city: address.city || '',
                                    state: address.state?.toUpperCase() || '',
                                    zip: address.zipCode || '',
                                    country: prev.address?.country || 'US'
                                  }
                                }));
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="col-span-2">
                              <Label htmlFor="address.city">City</Label>
                              <Input
                                id="address.city"
                                name="address.city"
                                value={formData.address?.city || ''}
                                onChange={handleInputChange}
                                placeholder="City"
                                className="mt-1.5"
                              />
                            </div>
                            <div>
                              <Label htmlFor="address.state">State</Label>
                              <Select
                                value={formData.address?.state || ''}
                                onValueChange={handleSelectChange('address.state')}
                              >
                                <SelectTrigger className="mt-1.5">
                                  <SelectValue placeholder="State" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60 overflow-y-auto">
                                  {US_STATES.map((state) => (
                                    <SelectItem key={state.value} value={state.value}>
                                      {state.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="address.zip">ZIP Code</Label>
                              <Input
                                id="address.zip"
                                name="address.zip"
                                value={formData.address?.zip || ''}
                                onChange={handleInputChange}
                                placeholder="ZIP"
                                className="mt-1.5"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="address.country">Country</Label>
                            <Select
                              value={formData.address?.country || 'US'}
                              onValueChange={handleSelectChange('address.country')}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60 overflow-y-auto">
                                {COUNTRIES.map((country) => (
                                  <SelectItem key={country.value} value={country.value}>
                                    {country.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </CardGlow>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Invoice Tab */}
            <TabsContent value="invoice" className="space-y-6 py-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div className="space-y-1">
                    <CardTitle>Invoice Customization</CardTitle>
                    <CardDescription>
                      Customize the appearance and behavior of your invoices
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="preview-mode" className="text-sm">{previewMode ? "Customize" : "Preview"}</Label>
                      <Switch
                        id="preview-mode"
                        checked={previewMode}
                        onCheckedChange={setPreviewMode}
                        
                      />
                    </div>
                    <Button
                      onClick={() => updateSettingsMutation.mutate(formData)}
                      disabled={updateSettingsMutation.isPending || (!hasUnsavedChanges && showSavedState)}
                      className={cn(
                        "shadow-sm transition-all hover:shadow-md",
                        showSavedState && !hasUnsavedChanges ? "bg-green-500 hover:bg-green-600" : ""
                      )}
                    >
                      {updateSettingsMutation.isPending ? (
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-r-foreground" />
                          <span>Saving...</span>
                        </div>
                      ) : showSavedState && !hasUnsavedChanges ? (
                        'Saved'
                      ) : (
                        'Save Settings'
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {previewMode ? (
                    <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6 mb-6">
                      <InvoicePreview
                        invoice={sampleInvoice}
                        companySettings={formData}
                        className="w-full"
                      />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Numbering & Format Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold">Numbering & Format</h3>
                          <p className="text-sm text-muted-foreground">Configure how your invoice numbers are generated</p>
                        </div>
                        
                        <Card className="border-dashed">
                          <CardHeader className="py-1.5 px-4">
                            <div className="flex flex-col">
                              <CardTitle className="text-base">Format Preview</CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0 pb-3">
                            <div
                              className={cn(
                                "text-center font-mono text-2xl font-semibold tracking-wide",
                                hasUnsavedChanges && "animate-[flash_1s_ease-in-out_3]"
                              )}
                            >
                              {(() => {
                                const prefix = formData.invoiceSettings?.invoicePrefix || '';
                                const datePrefix = formData.invoiceSettings?.enableDatePrefix
                                  ? `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}`
                                  : '';
                                const number = String(formData.invoiceSettings?.invoiceStartingNumber || 1000).padStart(3, '0');

                                // Build the format intelligently
                                let format = '';

                                // Add prefix
                                if (prefix) {
                                  format += prefix;
                                  // Add separator after prefix only if it doesn't already end with one
                                  if (!prefix.endsWith('-') && !prefix.endsWith('_')) {
                                    format += '-';
                                  }
                                }

                                // Add date prefix
                                if (datePrefix) {
                                  format += datePrefix;
                                  format += '-';
                                }

                                // Add number
                                format += number;

                                return format || number;
                              })()}
                            </div>
                          </CardContent>
                        </Card>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card>
                            <CardHeader className="space-y-1">
                              <CardTitle className="text-base">Starting Number</CardTitle>
                              <CardDescription>Initial invoice number</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Input
                                id="invoiceStartingNumber"
                                type="number"
                                name="invoiceSettings.invoiceStartingNumber"
                                value={formData.invoiceSettings?.invoiceStartingNumber || 1000}
                                onChange={handleInputChange}
                                min={1}
                              />
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="space-y-1">
                              <CardTitle className="text-base">Invoice Prefix</CardTitle>
                              <CardDescription>e.g., INV-001</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Input
                                id="invoicePrefix"
                                type="text"
                                name="invoiceSettings.invoicePrefix"
                                value={formData.invoiceSettings?.invoicePrefix || ''}
                                onChange={handleInputChange}
                                placeholder="e.g., INV"
                                className="uppercase"
                              />
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="space-y-1">
                              <CardTitle className="text-base">Date Prefix</CardTitle>
                              <CardDescription>e.g., 20240215-001</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center justify-between">
                                <Label htmlFor="enableDatePrefix">Include date in number</Label>
                                <Switch
                                  id="enableDatePrefix"
                                  checked={formData.invoiceSettings?.enableDatePrefix ?? false}
                                  onCheckedChange={handleSwitchChange('enableDatePrefix')}
                                  
                                />
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>

                      <Separator />

                      {/* Hide the settings sections when in preview mode */}
                      {!previewMode && (
                        <>
                          <Separator />

                          {/* Visual Settings Section */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold">Visual Settings</h3>
                              <Button variant="outline" size="sm" onClick={handleResetColors}>
                                Reset to Defaults
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {/* Logo Settings */}
                              <Card className="border-dashed">
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Company Logo</CardTitle>
                                  <CardDescription>Display your logo on invoices</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor="showLogo">Show on Invoice</Label>
                                    <Switch
                                      id="showLogo"
                                      checked={formData.invoiceSettings?.showLogo ?? false}
                                      onCheckedChange={handleSwitchChange('showLogo')}
                                      
                                    />
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Template Selector */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Invoice Template</CardTitle>
                                  <CardDescription>Choose your invoice look</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <Select
                                    value={formData.invoiceSettings?.template || 'professional'}
                                    onValueChange={(value) => handleSelectChange('invoiceSettings.template')(value)}
                                  >
                                    <SelectTrigger id="template">
                                      <SelectValue
                                        placeholder="Select template"
                                        className="w-full"
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="professional">Professional</SelectItem>
                                      <SelectItem value="classic">Classic</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </CardContent>
                              </Card>

                              {/* Contact Info Settings */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Contact Information</CardTitle>
                                  <CardDescription>Contact details on invoice</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor="showPhoneOnInvoice">Show Phone Number</Label>
                                    <Switch
                                      id="showPhoneOnInvoice"
                                      checked={formData.invoiceSettings?.showPhoneOnInvoice ?? true}
                                      onCheckedChange={handleSwitchChange('showPhoneOnInvoice')}
                                      
                                    />
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor="showEmailOnInvoice">Show Email Address</Label>
                                    <Switch
                                      id="showEmailOnInvoice"
                                      checked={formData.invoiceSettings?.showEmailOnInvoice ?? true}
                                      onCheckedChange={handleSwitchChange('showEmailOnInvoice')}
                                      
                                    />
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Primary Color */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Primary Color</CardTitle>
                                  <CardDescription>Main brand color</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div 
                                    className="group relative rounded-lg border p-4 hover:border-primary/50 transition-colors cursor-pointer"
                                    onClick={() => {
                                      const input = document.getElementById('primaryColor');
                                      if (input) input.click();
                                    }}
                                  >
                                    <div 
                                      className="h-8 rounded-md transition-all"
                                      style={{ backgroundColor: formData.invoiceSettings?.colors?.primary || '#4F46E5' }}
                                    />
                                    <Input
                                      id="primaryColor"
                                      name="invoiceSettings.colors.primary"
                                      type="color"
                                      value={formData.invoiceSettings?.colors?.primary || '#4F46E5'}
                                      onChange={handleInputChange}
                                      className="sr-only"
                                    />
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Secondary Color */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Secondary Color</CardTitle>
                                  <CardDescription>Accent color</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div 
                                    className="group relative rounded-lg border p-4 hover:border-primary/50 transition-colors cursor-pointer"
                                    onClick={() => {
                                      const input = document.getElementById('secondaryColor');
                                      if (input) input.click();
                                    }}
                                  >
                                    <div 
                                      className="h-8 rounded-md transition-all"
                                      style={{ backgroundColor: formData.invoiceSettings?.colors?.secondary || '#6B7280' }}
                                    />
                                    <Input
                                      id="secondaryColor"
                                      name="invoiceSettings.colors.secondary"
                                      type="color"
                                      value={formData.invoiceSettings?.colors?.secondary || '#6B7280'}
                                      onChange={handleInputChange}
                                      className="sr-only"
                                    />
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Header Text Color */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Header Text</CardTitle>
                                  <CardDescription>Text color for headers</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div 
                                    className="group relative rounded-lg border p-4 hover:border-primary/50 transition-colors cursor-pointer"
                                    onClick={() => {
                                      const input = document.getElementById('headerTextColor');
                                      if (input) input.click();
                                    }}
                                  >
                                    <div className="h-8 rounded-md flex items-center justify-center text-sm font-semibold transition-all"
                                      style={{ 
                                        backgroundColor: '#ffffff',
                                        color: formData.invoiceSettings?.colors?.headerText || '#111827'
                                      }}>
                                      Sample Text
                                    </div>
                                    <Input
                                      id="headerTextColor"
                                      name="invoiceSettings.colors.headerText"
                                      type="color"
                                      value={formData.invoiceSettings?.colors?.headerText || '#111827'}
                                      onChange={handleInputChange}
                                      className="sr-only"
                                    />
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Table Header Colors */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Table Header</CardTitle>
                                  <CardDescription>Colors for table headers</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                      <Label className="text-xs mb-2 block">Background</Label>
                                      <div 
                                        className="h-8 rounded-md cursor-pointer"
                                        style={{ backgroundColor: formData.invoiceSettings?.colors?.tableHeader || '#F3F4F6' }}
                                        onClick={() => {
                                          const input = document.getElementById('tableHeaderColor');
                                          if (input) input.click();
                                        }}
                                      />
                                      <Input
                                        id="tableHeaderColor"
                                        name="invoiceSettings.colors.tableHeader"
                                        type="color"
                                        value={formData.invoiceSettings?.colors?.tableHeader || '#F3F4F6'}
                                        onChange={handleInputChange}
                                        className="sr-only"
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <Label className="text-xs mb-2 block">Text</Label>
                                      <div 
                                        className="h-8 rounded-md cursor-pointer flex items-center justify-center"
                                        style={{ 
                                          backgroundColor: formData.invoiceSettings?.colors?.tableHeader || '#F3F4F6',
                                          color: formData.invoiceSettings?.colors?.tableHeaderText || '#111827'
                                        }}
                                        onClick={() => {
                                          const input = document.getElementById('tableHeaderTextColor');
                                          if (input) input.click();
                                        }}
                                      >
                                        Aa
                                      </div>
                                      <Input
                                        id="tableHeaderTextColor"
                                        name="invoiceSettings.colors.tableHeaderText"
                                        type="color"
                                        value={formData.invoiceSettings?.colors?.tableHeaderText || '#111827'}
                                        onChange={handleInputChange}
                                        className="sr-only"
                                      />
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>

                              {/* Button Colors */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Button Colors</CardTitle>
                                  <CardDescription>Colors for action buttons</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                      <Label className="text-xs mb-2 block">Background</Label>
                                      <div 
                                        className="h-8 rounded-md cursor-pointer"
                                        style={{ backgroundColor: formData.invoiceSettings?.colors?.buttonBackground || '#22C55E' }}
                                        onClick={() => {
                                          const input = document.getElementById('buttonBackgroundColor');
                                          if (input) input.click();
                                        }}
                                      />
                                      <Input
                                        id="buttonBackgroundColor"
                                        name="invoiceSettings.colors.buttonBackground"
                                        type="color"
                                        value={formData.invoiceSettings?.colors?.buttonBackground || '#22C55E'}
                                        onChange={handleInputChange}
                                        className="sr-only"
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <Label className="text-xs mb-2 block">Text</Label>
                                      <div 
                                        className="h-8 rounded-md cursor-pointer flex items-center justify-center"
                                        style={{ 
                                          backgroundColor: formData.invoiceSettings?.colors?.buttonBackground || '#22C55E',
                                          color: formData.invoiceSettings?.colors?.linkText || '#FFFFFF'
                                        }}
                                        onClick={() => {
                                          const input = document.getElementById('linkTextColor');
                                          if (input) input.click();
                                        }}
                                      >
                                        Aa
                                      </div>
                                      <Input
                                        id="linkTextColor"
                                        name="invoiceSettings.colors.linkText"
                                        type="color"
                                        value={formData.invoiceSettings?.colors?.linkText || '#FFFFFF'}
                                        onChange={handleInputChange}
                                        className="sr-only"
                                      />
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>

                            {/* Color Scheme Tips */}
                            <Card className="bg-muted/50 border-primary/20 border-2">
                              <CardHeader className="space-y-1">
                                <CardTitle className="text-base">Color Scheme Tips</CardTitle>
                                <CardDescription>Best practices for color selection</CardDescription>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                <div className="p-3 bg-white rounded-lg border text-sm">
                                  <p className="font-medium mb-2">Tips for professional invoices:</p>
                                  <ul className="space-y-1.5 list-disc pl-5 text-muted-foreground">
                                    <li>Use your brand's primary color sparingly for accents</li>
                                    <li>Maintain high contrast for text elements (4.5:1 ratio minimum)</li>
                                    <li>Choose complementary colors for tables and headers</li>
                                    <li>Keep buttons visually distinct from other elements</li>
                                    <li>Consider colorblind users - avoid red/green only distinctions</li>
                                    <li>Ensure your color scheme works with all templates</li>
                                  </ul>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <div className="p-2 bg-blue-50 border border-blue-200 rounded flex items-center">
                                    <div className="w-4 h-4 rounded-full bg-blue-600 mr-2"></div>
                                    <span className="text-xs">Professional: Blue tones</span>
                                  </div>
                                  <div className="p-2 bg-indigo-50 border border-indigo-200 rounded flex items-center">
                                    <div className="w-4 h-4 rounded-full bg-indigo-600 mr-2"></div>
                                    <span className="text-xs">Modern: Purple tones</span>
                                  </div>
                                  <div className="p-2 bg-gray-50 border border-gray-200 rounded flex items-center">
                                    <div className="w-4 h-4 rounded-full bg-gray-700 mr-2"></div>
                                    <span className="text-xs">Classic: Neutral tones</span>
                                  </div>
                                  <div className="p-2 bg-emerald-50 border border-emerald-200 rounded flex items-center">
                                    <div className="w-4 h-4 rounded-full bg-emerald-600 mr-2"></div>
                                    <span className="text-xs">Contemporary: Green tones</span>
                                  </div>
                                </div>
                                
                                <div className="rounded-lg border overflow-hidden">
                                  <div className="bg-gray-100 px-3 py-1.5 text-xs font-medium">Example Color Combinations</div>
                                  <div className="divide-y">
                                    <div className="p-2 flex items-center">
                                      <div className="flex space-x-1 mr-3">
                                        <div className="w-4 h-4 rounded-sm bg-blue-600"></div>
                                        <div className="w-4 h-4 rounded-sm bg-gray-100"></div>
                                        <div className="w-4 h-4 rounded-sm bg-blue-100"></div>
                                      </div>
                                      <span className="text-xs text-gray-600">Professional Blue</span>
                                    </div>
                                    <div className="p-2 flex items-center">
                                      <div className="flex space-x-1 mr-3">
                                        <div className="w-4 h-4 rounded-sm bg-slate-800"></div>
                                        <div className="w-4 h-4 rounded-sm bg-slate-100"></div>
                                        <div className="w-4 h-4 rounded-sm bg-amber-400"></div>
                                      </div>
                                      <span className="text-xs text-gray-600">Corporate Elegance</span>
                                    </div>
                                    <div className="p-2 flex items-center">
                                      <div className="flex space-x-1 mr-3">
                                        <div className="w-4 h-4 rounded-sm bg-emerald-600"></div>
                                        <div className="w-4 h-4 rounded-sm bg-gray-50"></div>
                                        <div className="w-4 h-4 rounded-sm bg-emerald-100"></div>
                                      </div>
                                      <span className="text-xs text-gray-600">Fresh & Modern</span>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          {/* Rest of the sections remain the same, but update Platform Fee to Credit Cards */}
                          <Separator />

                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Payment Settings</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Payment Terms</CardTitle>
                                  <CardDescription>Default payment period</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <Select
                                    value={formData.invoiceSettings?.defaultDueTerms || 'net_30'}
                                    onValueChange={(value) => handleSelectChange('invoiceSettings.defaultDueTerms')(value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select payment terms" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                                      <SelectItem value="net_15">Net 15</SelectItem>
                                      <SelectItem value="net_30">Net 30</SelectItem>
                                      <SelectItem value="net_60">Net 60</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </CardContent>
                              </Card>

                              {/* Platform Fee Toggle */}
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Platform Fees</CardTitle>
                                  <CardDescription>Show processing fees on invoices</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor="enablePlatformFee">Display Platform Fees</Label>
                                    <Switch
                                      id="enablePlatformFee"
                                      checked={formData.invoiceSettings?.enablePlatformFee ?? false}
                                      onCheckedChange={handleSwitchChange('enablePlatformFee')}
                                      
                                    />
                                  </div>
                                  {formData.invoiceSettings?.enablePlatformFee && (
                                    <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
                                      <p>Current fee: {formData.platformFee || 0}%</p>
                                      <p className="mt-1">Adjust fee percentage in Payment settings tab</p>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>


                              <Card className="md:col-span-2 lg:col-span-1">
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Available Payment Methods</CardTitle>
                                  <CardDescription>Available payment options for your invoices</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                                    <div className="flex items-center justify-between">
                                      <Label htmlFor="credit_card">Credit Card Payments</Label>
                                      <Switch
                                        id="credit_card"
                                        checked={formData.invoiceSettings?.paymentMethods?.credit_card ?? true}
                                        onCheckedChange={(checked) => handlePaymentMethodChange('credit_card', checked)}
                                        
                                        disabled={true}
                                      />
                                    </div>
                                    {isAchGatewayEnabled && (
                                      <div className="flex items-center justify-between">
                                        <Label htmlFor="ach">ACH Payments</Label>
                                        <Switch
                                          id="ach"
                                          checked={formData.invoiceSettings?.paymentMethods?.ach ?? false}
                                          onCheckedChange={(checked) => handlePaymentMethodChange('ach', checked)}
                                        />
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                      <Label htmlFor="paypal">PayPal</Label>
                                      <Switch
                                        id="paypal"
                                        checked={formData.invoiceSettings?.paymentMethods?.paypal ?? false}
                                        onCheckedChange={(checked) => handlePaymentMethodChange('paypal', checked)}
                                        
                                      />
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <Label htmlFor="check">Check Payments</Label>
                                      <Switch
                                        id="check"
                                        checked={formData.invoiceSettings?.paymentMethods?.check ?? false}
                                        onCheckedChange={(checked) => handlePaymentMethodChange('check', checked)}
                                        
                                      />
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>
                          </div>

                          {/* Rest of the existing sections */}
                          <Separator />

                          {/* Content Settings */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Additional Content</h3>
                            <div className="grid grid-cols-1 gap-4">
                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Terms & Conditions</CardTitle>
                                  <CardDescription>Displayed at the bottom of every invoice when enabled. Use for payment terms, late fees, legal disclaimers.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <Label htmlFor="showTermsOnInvoice">Display on invoice</Label>
                                      <Switch
                                        id="showTermsOnInvoice"
                                        checked={formData.invoiceSettings?.showTermsOnInvoice ?? true}
                                        onCheckedChange={handleSwitchChange('showTermsOnInvoice')}

                                      />
                                    </div>
                                    <RichTextEditor
                                      value={formData.invoiceSettings?.customFooterText || ''}
                                      onChange={(html) => handleNestedUpdate('invoiceSettings', 'customFooterText', html)}
                                      placeholder="Enter terms & conditions..."
                                      minHeight="100px"
                                    />
                                  </div>
                                </CardContent>
                              </Card>

                              <Card>
                                <CardHeader className="space-y-1">
                                  <CardTitle className="text-base">Invoice Notes</CardTitle>
                                  <CardDescription>Pre-filled into each new invoice's notes field. Can be edited per invoice. Use for thank-you messages, project details, or special instructions.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <Label htmlFor="showNotesOnInvoice">Display on invoice</Label>
                                      <Switch
                                        id="showNotesOnInvoice"
                                        checked={formData.invoiceSettings?.showNotesOnInvoice ?? true}
                                        onCheckedChange={handleSwitchChange('showNotesOnInvoice')}

                                      />
                                    </div>
                                    <RichTextEditor
                                      value={formData.invoiceSettings?.defaultNotes || ''}
                                      onChange={(html) => handleNestedUpdate('invoiceSettings', 'defaultNotes', html)}
                                      placeholder="Enter default notes..."
                                      minHeight="100px"
                                    />
                                  </div>
                                </CardContent>
                              </Card>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payment Tab */}
            <TabsContent value="payment">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold text-primary">Payment Settings</h2>
                    <p className="text-sm text-muted-foreground">Configure payment methods and processing options</p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        // Save platform fee and other settings
                        const update: Partial<CompanyProfile> = {
                          platformFee: formData.platformFee,
                          defaultTaxRate: formData.defaultTaxRate,
                          wikiPayitSettings: formData.wikiPayitSettings,
                          enableRecurringInvoices: formData.enableRecurringInvoices,
                          enableInstallmentInvoices: formData.enableInstallmentInvoices
                        };
                        
                        // Save invoice settings separately
                        await updateSettingsMutation.mutateAsync(update);
                        
                        // Handle invoice settings separately
                        if (formData.invoiceSettings) {
                          await updateCompanySettings(companyId, {
                            invoiceSettings: formData.invoiceSettings
                          } as any);
                        }

                        showNotification({
                          title: "Settings Saved",
                          message: "Payment settings have been updated successfully",
                          type: "success"
                        });
                        
                        // Reset editable fields after saving
                        setEditableFields({
                          tokenization: false,
                          security: false
                        });
                      } catch (error) {
                        showNotification({
                          title: "Save Failed",
                          message: "Failed to save payment settings. Please try again.",
                          type: "error"
                        });
                      }
                    }}
                    disabled={updateSettingsMutation.isPending || (!hasUnsavedChanges && showSavedState)}
                    className={cn(
                      "shadow-sm transition-all hover:shadow-md",
                      showSavedState && !hasUnsavedChanges ? "bg-green-500 hover:bg-green-600" : "",
                      "px-4 gap-2"
                    )}
                  >
                    {updateSettingsMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-r-foreground" />
                        <span>Saving...</span>
                      </div>
                    ) : showSavedState && !hasUnsavedChanges ? (
                      <span className="flex items-center gap-2">
                        <CheckIcon className="h-4 w-4" />
                        Saved
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Save Changes
                      </span>
                    )}
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-6">
                  {/* Left Column - General Payment Settings */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold">General Settings</h3>
                      <div className="bg-yellow-50 text-yellow-800 text-xs rounded-full px-2 py-0.5 border border-yellow-200">
                        Customer Facing
                      </div>
                    </div>

                    {/* Platform Fee Settings */}
                    <CardGlow color="hsl(var(--villavie-gold))" className="border shadow-sm">
                      <div className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <Label className="text-base font-semibold">Platform Fees</Label>
                            <p className="text-sm text-muted-foreground">Additional charges for payment processing</p>
                          </div>
                          <Switch
                            id="enablePlatformFee"
                            checked={formData.invoiceSettings?.enablePlatformFee ?? false}
                            onCheckedChange={handleSwitchChange('enablePlatformFee')}
                            
                          />
                        </div>
                        {formData.invoiceSettings?.enablePlatformFee && (
                          <div className="pt-4 mt-4 border-t border-border space-y-2">
                            <Label htmlFor="platformFee" className="flex items-center space-x-2">
                              <span>Fee Percentage</span>
                              <div className="bg-amber-50 text-amber-700 text-xs px-1.5 py-0.5 rounded-sm">Applied at checkout</div>
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                id="platformFee"
                                name="platformFee"
                                type="number"
                                value={formData.platformFee || 0}
                                onChange={handleNumberInputChange}
                                min={0}
                                max={100}
                                step={0.1}
                                className="flex-1"
                              />
                              <span className="flex-shrink-0 font-medium">%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardGlow>

                    {/* Recurring Payment Settings */}
                    <CardGlow color="hsl(var(--villavie-gold))" className="border shadow-sm">
                      <div className="p-6 space-y-4">
                        <div className="space-y-1 mb-4">
                          <Label className="text-base font-semibold">Recurring & Installment Payments</Label>
                          <p className="text-sm text-muted-foreground">Configure advanced payment options</p>
                        </div>
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label htmlFor="enableRecurringInvoices" className="font-medium">Recurring Invoices</Label>
                              <p className="text-xs text-muted-foreground mt-1">Allow creation of invoices that automatically rebill on a schedule</p>
                              {tenantCompany && tenantCompany.enableRecurringInvoices === false && (
                                <p className="text-xs text-gray-400 mt-2">To enable, please contact support@{window.location.hostname} to enable this feature.</p>
                              )}
                            </div>
                            <Switch
                              id="enableRecurringInvoices"
                              checked={formData.enableRecurringInvoices ?? false}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  enableRecurringInvoices: checked
                                }));
                                setHasUnsavedChanges(true);
                                setShowSavedState(false);
                              }}
                              
                              disabled={tenantCompany && tenantCompany.enableRecurringInvoices === false}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <Label htmlFor="enableInstallmentInvoices" className="font-medium">Installment Invoices</Label>
                              <p className="text-xs text-muted-foreground mt-1">Allow splitting invoice payments into multiple installments</p>
                              {tenantCompany && tenantCompany.enableInstallmentInvoices === false && (
                                <p className="text-xs text-gray-400 mt-2">To enable, please contact support@{window.location.hostname} to enable this feature.</p>
                              )}
                            </div>
                            <Switch
                              id="enableInstallmentInvoices"
                              checked={formData.enableInstallmentInvoices ?? false}
                              onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                  ...prev,
                                  enableInstallmentInvoices: checked
                                }));
                                setHasUnsavedChanges(true);
                                setShowSavedState(false);
                              }}
                              
                              disabled={tenantCompany && tenantCompany.enableInstallmentInvoices === false}
                            />
                          </div>
                        </div>
                        {formData.enableRecurringInvoices && (
                          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm mt-4">
                            <p className="flex items-center">
                              <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                              Recurring payments enabled! You can now create subscription invoices.
                            </p>
                          </div>
                        )}
                      </div>
                    </CardGlow>

                    {/* Tax Rate Settings */}
                    <CardGlow color="hsl(var(--villavie-gold))" className="border shadow-sm">
                      <div className="p-6 space-y-4">
                        <div className="space-y-1">
                          <Label className="text-base font-semibold">Default Tax Rate</Label>
                          <p className="text-sm text-muted-foreground">Standard tax rate for new invoices</p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center gap-2 w-32">
                            <Input
                              id="defaultTaxRate"
                              name="defaultTaxRate"
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={formData.defaultTaxRate || ''}
                              onChange={handleNumberInputChange}
                              className="w-full"
                            />
                            <span className="text-sm font-medium">%</span>
                          </div>
                          
                          <div className="text-sm text-muted-foreground">
                            Applied to all new invoices by default
                          </div>
                        </div>
                      </div>
                    </CardGlow>

                    {/* Available Payment Methods */}
                    <CardGlow color="hsl(var(--villavie-gold))" className="border shadow-sm">
                      <div className="p-6 space-y-5">
                        <div className="space-y-1">
                          <Label className="text-base font-semibold">Available Payment Methods</Label>
                          <p className="text-sm text-muted-foreground">Select payment methods available for your customers</p>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mt-2">
                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center",
                              formData.invoiceSettings?.paymentMethods?.credit_card ?
                                "bg-primary" :
                                "bg-muted"
                            )}>
                              <CreditCard className={cn(
                                "w-5 h-5",
                                formData.invoiceSettings?.paymentMethods?.credit_card ?
                                  "text-white" :
                                  "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1">
                              <Label htmlFor="credit_card" className="font-medium cursor-pointer">Credit Card</Label>
                              <p className="text-xs text-muted-foreground">Visa, Mastercard, etc.</p>
                            </div>
                            <Switch
                              id="credit_card"
                              checked={formData.invoiceSettings?.paymentMethods?.credit_card ?? true}
                              onCheckedChange={(checked) => handlePaymentMethodChange('credit_card', checked)}

                              disabled={true}
                            />
                          </div>

                          {isAchGatewayEnabled && (
                            <div className="flex items-center space-x-3">
                              <div className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center",
                                formData.invoiceSettings?.paymentMethods?.ach ?
                                  "bg-primary" :
                                  "bg-muted"
                              )}>
                                <Building2 className={cn(
                                  "w-5 h-5",
                                  formData.invoiceSettings?.paymentMethods?.ach ?
                                    "text-white" :
                                    "text-muted-foreground"
                                )} />
                              </div>
                              <div className="flex-1">
                                <Label htmlFor="ach" className="font-medium cursor-pointer">ACH Transfer</Label>
                                <p className="text-xs text-muted-foreground">Bank transfers</p>
                              </div>
                              <Switch
                                id="ach"
                                checked={formData.invoiceSettings?.paymentMethods?.ach ?? false}
                                onCheckedChange={(checked) => handlePaymentMethodChange('ach', checked)}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center",
                              formData.invoiceSettings?.paymentMethods?.paypal ?
                                "bg-primary" :
                                "bg-muted"
                            )}>
                              <CreditCard className={cn(
                                "w-5 h-5",
                                formData.invoiceSettings?.paymentMethods?.paypal ?
                                  "text-white" :
                                  "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1">
                              <Label htmlFor="paypal" className="font-medium cursor-pointer">PayPal</Label>
                              <p className="text-xs text-muted-foreground">Online payments</p>
                            </div>
                            <Switch
                              id="paypal"
                              checked={formData.invoiceSettings?.paymentMethods?.paypal ?? false}
                              onCheckedChange={(checked) => handlePaymentMethodChange('paypal', checked)}

                            />
                          </div>

                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center",
                              formData.invoiceSettings?.paymentMethods?.check ?
                                "bg-primary" :
                                "bg-muted"
                            )}>
                              <FileText className={cn(
                                "w-5 h-5",
                                formData.invoiceSettings?.paymentMethods?.check ?
                                  "text-white" :
                                  "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1">
                              <Label htmlFor="check" className="font-medium cursor-pointer">Check</Label>
                              <p className="text-xs text-muted-foreground">Physical payments</p>
                            </div>
                            <Switch
                              id="check"
                              checked={formData.invoiceSettings?.paymentMethods?.check ?? false}
                              onCheckedChange={(checked) => handlePaymentMethodChange('check', checked)}
                              
                            />
                          </div>
                        </div>
                      </div>
                    </CardGlow>
                  </div>

                  {/* Right Column - Payment Gateway Settings */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold">Payment Gateway</h3>
                      <div className="bg-amber-50 text-amber-800 text-xs rounded-full px-2 py-0.5 border border-amber-200">
                        API Configuration
                      </div>
                    </div>

                    {/* WikiPayit Credentials section - Hidden from customer view */}
                    {/* eslint-disable-next-line no-constant-binary-expression */}
                    {false && (
                    <CardGlow color="hsl(var(--villavie-gold))" className="border shadow-sm">
                      <div className="p-6 space-y-5">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <Label className="text-base font-semibold">WikiPayit Credentials</Label>
                            <div
                              className={cn(
                                "text-xs px-1.5 py-0.5 rounded-sm border",
                                "bg-blue-50 text-blue-700 border-blue-100"
                              )}
                            >
                              Secure
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">Secure payment processing configuration</p>
                        </div>
                        <div className="space-y-5 mt-2">
                          <div className="space-y-2">
                            <Label htmlFor="wikiPayitUsername">Account Username</Label>
                            <Input
                              id="wikiPayitUsername"
                              name="wikiPayitSettings.username"
                              type="text"
                              value={formData.wikiPayitSettings?.username || ''}
                              onChange={handleInputChange}
                              placeholder="Enter your WikiPayit username"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="wikiPayitTokenizationKey">
                              Tokenization Key
                              {formData.wikiPayitSettings?.tokenizationKey && (
                                <span className="ml-2 text-xs text-muted-foreground">(Unlocked)</span>
                              )}
                            </Label>
                            <div className="relative">
                              <Input
                                id="wikiPayitTokenizationKey"
                                name="wikiPayitSettings.tokenizationKey"
                                type="password"
                                value={formData.wikiPayitSettings?.tokenizationKey || ''}
                                onChange={handleInputChange}
                                placeholder="Enter your tokenization key"
                                className={cn(
                                  "font-mono pr-10",
                                  formData.wikiPayitSettings?.tokenizationKey && !editableFields.tokenization && "cursor-pointer bg-muted"
                                )}
                                readOnly={!!formData.wikiPayitSettings?.tokenizationKey && !editableFields.tokenization}
                                onClick={() => handleCredentialFieldClick('tokenization')}
                              />
                              {!formData.wikiPayitSettings?.tokenizationKey && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                  onClick={() => {
                                    const input = document.getElementById('wikiPayitTokenizationKey') as HTMLInputElement;
                                    if (input) {
                                      input.type = input.type === 'password' ? 'text' : 'password';
                                    }
                                  }}
                                >
                                  👁️
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="wikiPayitSecurityKey">
                              Security Key (API)
                              {formData.wikiPayitSettings?.securityKey && (
                                <span className="ml-2 text-xs text-muted-foreground">(Unlocked)</span>
                              )}
                            </Label>
                            <div className="relative">
                              <Input
                                id="wikiPayitSecurityKey"
                                name="wikiPayitSettings.securityKey"
                                type="password"
                                value={formData.wikiPayitSettings?.securityKey || ''}
                                onChange={handleInputChange}
                                placeholder="Enter your security key"
                                className={cn(
                                  "font-mono pr-10",
                                  formData.wikiPayitSettings?.securityKey && !editableFields.security && "cursor-pointer bg-muted"
                                )}
                                readOnly={!!formData.wikiPayitSettings?.securityKey && !editableFields.security}
                                onClick={() => handleCredentialFieldClick('security')}
                              />
                              {!formData.wikiPayitSettings?.securityKey && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                  onClick={() => {
                                    const input = document.getElementById('wikiPayitSecurityKey') as HTMLInputElement;
                                    if (input) {
                                      input.type = input.type === 'password' ? 'text' : 'password';
                                    }
                                  }}
                                >
                                  👁️
                                </Button>
                              )}
                            </div>
                          </div>

                          {formData.wikiPayitSettings?.tokenizationKey && (
                            <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                              Click on a credential field to unlock for editing
                            </p>
                          )}
                        </div>
                      </div>
                    </CardGlow>
                    )}
                    {/* End WikiPayit Credentials section */}

                    {/* Password Dialog */}
                    {/* eslint-disable-next-line no-constant-binary-expression */}
                    {false && showVerificationDialog && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <Card className="w-full max-w-md">
                          <CardHeader>
                            <CardTitle>Enter Password</CardTitle>
                            <CardDescription>
                              Please enter the password to modify payment credentials
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="credential-password">Password</Label>
                                <Input
                                  id="credential-password"
                                  type="password"
                                  value={passwordAttempt}
                                  onChange={(e) => setPasswordAttempt(e.target.value)}
                                  placeholder="Enter password"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handlePasswordVerify();
                                    }
                                  }}
                                  autoFocus
                                  className="w-full"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setShowVerificationDialog(false);
                                    setPasswordAttempt('');
                                    setSelectedField(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button onClick={handlePasswordVerify}>
                                  Unlock
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    <div className="flex items-center p-4 rounded-lg bg-primary/10 border border-primary/20 text-gray-600">
                      <div className="rounded-full bg-primary/10 p-2 mr-4">
                        <CreditCard className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Need WikiPayit credentials?</p>
                        <p className="text-sm text-muted-foreground">
                          Contact support to obtain your API credentials or learn more about payment processing options.
                        </p>
                      </div>
                    </div>

                    <div className="bg-white/60 backdrop-blur-sm border rounded-lg p-5 shadow-sm space-y-4">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium">Accepted Card Types</h4>
                        <div 
                          className={cn(
                            "text-xs px-1.5 py-0.5 rounded-sm",
                            "bg-blue-50 text-blue-700"
                          )}
                        >
                          Automatic
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 justify-between">
                        <div className="flex space-x-2">
                          <div className="w-10 h-6 bg-blue-600 rounded"></div>
                          <div className="w-10 h-6 bg-red-500 rounded"></div>
                          <div className="w-10 h-6 bg-yellow-400 rounded"></div>
                          <div className="w-10 h-6 bg-green-500 rounded"></div>
                        </div>
                        <p className="text-xs text-muted-foreground">All major credit cards accepted</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email" className="space-y-6">
              <div className="grid gap-6">
                {/* Email Templates Section */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-semibold">Email Templates</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Customize your email templates for different scenarios
                      </p>
                    </div>
                    <div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => window.open('/docs/email-templates.md', '_blank')}
                      >
                        <FileText className="h-4 w-4" />
                        Documentation
                      </Button>
                    </div>
                  </div>

                  <Tabs defaultValue="settings" className="w-full">
                    <TabsList className="w-full justify-start mb-6">
                      <TabsTrigger value="settings" className="flex items-center gap-2">
                        <Settings2 className="h-4 w-4" />
                        Settings
                      </TabsTrigger>
                      <TabsTrigger value="payment-request" className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Payment Request
                      </TabsTrigger>
                      <TabsTrigger value="invoice" className="flex items-center gap-2">
                        <Receipt className="h-4 w-4" />
                        Reminder
                      </TabsTrigger>
                      <TabsTrigger value="thank-you" className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Thank You
                      </TabsTrigger>
                      <TabsTrigger value="donation-thank-you" className="flex items-center gap-2">
                        <Gift className="h-4 w-4" />
                        Donation Thank You
                      </TabsTrigger>
                      <TabsTrigger value="refund" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Refund
                      </TabsTrigger>
                      <TabsTrigger value="failed" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Failed Payment
                      </TabsTrigger>
                      <TabsTrigger value="recurring-invoice" className="flex items-center gap-2">
                        <RepeatIcon className="h-4 w-4" />
                        Recurring Invoice
                      </TabsTrigger>
                    </TabsList>

                    {/* Payment Request Template */}
                    <TabsContent value="payment-request" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-medium">Payment Request Email</h3>
                              <p className="text-sm text-muted-foreground">Template for requesting payment from customers</p>
                            </div>
                          </div>
                          <EmailEditor
                            content={formData.emailSettings?.paymentRequest || defaultTemplates.paymentRequest}
                            onChange={(content) => handleEmailTemplateChange('paymentRequest', content)}
                            placeholder="Write your payment request email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.paymentRequest || defaultTemplates.paymentRequest}
                                invoice={previewData}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.paymentRequestSubject || defaultTemplates.paymentRequestSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('paymentRequest', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('paymentRequest')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Payment Request Template'}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Thank You Template */}
                    <TabsContent value="thank-you" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-medium">Payment Thank You Email</h3>
                              <p className="text-sm text-muted-foreground">Template for confirming successful payments</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch 
                                id="enable-thank-you-email"
                                checked={formData.emailSettings?.enableThankYouEmail || false}
                                onCheckedChange={(checked) => {
                                  setFormData(prevState => ({
                                    ...prevState,
                                    emailSettings: {
                                      ...(prevState.emailSettings || {}),
                                      enableThankYouEmail: checked
                                    }
                                  }) as CompanySettingsWithLogo);
                                }}
                              />
                              <Label htmlFor="enable-thank-you-email">
                                <span className={formData.emailSettings?.enableThankYouEmail ? "font-medium" : "text-muted-foreground"}>
                                  Send automatically when invoice is paid
                                </span>
                              </Label>
                            </div>
                          </div>
                        <EmailEditor
                            content={formData.emailSettings?.paymentThankYou || defaultTemplates.paymentThankYou}
                            onChange={(content) => handleEmailTemplateChange('paymentThankYou', content)}
                            placeholder="Write your payment thank you email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.paymentThankYou || defaultTemplates.paymentThankYou}
                                invoice={previewData}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.paymentThankYouSubject || defaultTemplates.paymentThankYouSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('paymentThankYou', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('paymentThankYou')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Thank You Template'}
                            </Button>
                      </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Refund Template */}
                    <TabsContent value="refund" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-medium">Refund Issued Email</h3>
                              <p className="text-sm text-muted-foreground">Template for confirming refund issuance</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch 
                                id="enable-refund-email"
                                checked={formData.emailSettings?.enableRefundEmail || false}
                                onCheckedChange={(checked) => {
                                  setFormData(prevState => ({
                                    ...prevState,
                                    emailSettings: {
                                      ...(prevState.emailSettings || {}),
                                      enableRefundEmail: checked
                                    }
                                  }) as CompanySettingsWithLogo);
                                }}
                              />
                              <Label htmlFor="enable-refund-email">
                                <span className={formData.emailSettings?.enableRefundEmail ? "font-medium" : "text-muted-foreground"}>
                                  Send automatically when refund is processed
                                </span>
                              </Label>
                            </div>
                          </div>
                        <EmailEditor
                            content={formData.emailSettings?.refundIssued || defaultTemplates.refundIssued}
                            onChange={(content) => handleEmailTemplateChange('refundIssued', content)}
                            placeholder="Write your refund issued email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.refundIssued || defaultTemplates.refundIssued}
                                invoice={previewData}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.refundIssuedSubject || defaultTemplates.refundIssuedSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('refundIssued', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('refundIssued')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Refund Template'}
                            </Button>
                      </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Donation Thank You Template */}
                    <TabsContent value="donation-thank-you" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-medium">Donation Thank You Email</h3>
                              <p className="text-sm text-muted-foreground">Template for thanking donors for their contributions</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch 
                                id="enable-donation-thank-you-email"
                                checked={formData.emailSettings?.enableDonationThankYouEmail || false}
                                onCheckedChange={(checked) => {
                                  setFormData(prevState => ({
                                    ...prevState,
                                    emailSettings: {
                                      ...(prevState.emailSettings || {}),
                                      enableDonationThankYouEmail: checked
                                    }
                                  }) as CompanySettingsWithLogo);
                                }}
                              />
                              <Label htmlFor="enable-donation-thank-you-email">
                                <span className={formData.emailSettings?.enableDonationThankYouEmail ? "font-medium" : "text-muted-foreground"}>
                                  Send automatically when donation is received
                                </span>
                              </Label>
                            </div>
                          </div>
                        <EmailEditor
                            content={formData.emailSettings?.donationThankYou || defaultTemplates.donationThankYou}
                            onChange={(content) => handleEmailTemplateChange('donationThankYou', content)}
                            placeholder="Write your donation thank you email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.donationThankYou || defaultTemplates.donationThankYou}
                                invoice={{
                                  ...previewData,
                                  invoiceNumber: 'DON-123456',
                                  total: 100.00,
                                }}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.donationThankYouSubject || defaultTemplates.donationThankYouSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('donationThankYou', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('donationThankYou')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Donation Thank You Template'}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Failed Payment Template */}
                    <TabsContent value="failed" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-medium">Payment Failed Email</h3>
                            <p className="text-sm text-muted-foreground">Template for notifying customers of failed payments</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch 
                                id="enable-failed-payment-email"
                                checked={formData.emailSettings?.enableFailedPaymentEmail || false}
                                onCheckedChange={(checked) => {
                                  setFormData(prevState => ({
                                    ...prevState,
                                    emailSettings: {
                                      ...(prevState.emailSettings || {}),
                                      enableFailedPaymentEmail: checked
                                    }
                                  }) as CompanySettingsWithLogo);
                                }}
                              />
                              <Label htmlFor="enable-failed-payment-email">
                                <span className={formData.emailSettings?.enableFailedPaymentEmail ? "font-medium" : "text-muted-foreground"}>
                                  Send automatically when payment fails
                                </span>
                              </Label>
                            </div>
                          </div>
                        <EmailEditor
                            content={formData.emailSettings?.paymentFailed || defaultTemplates.paymentFailed}
                            onChange={(content) => handleEmailTemplateChange('paymentFailed', content)}
                            placeholder="Write your payment failed email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.paymentFailed || defaultTemplates.paymentFailed}
                                invoice={previewData}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.paymentFailedSubject || defaultTemplates.paymentFailedSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('paymentFailed', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('paymentFailed')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Failed Payment Template'}
                            </Button>
                      </div>
                    </div>
              </Card>
            </TabsContent>

                    {/* Recurring Invoice Template */}
                    <TabsContent value="recurring-invoice" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-medium">Recurring Invoice Email</h3>
                            <p className="text-sm text-muted-foreground">Template for sending recurring invoice notifications</p>
                          </div>
                        <EmailEditor
                            content={formData.emailSettings?.recurringInvoice || defaultTemplates.recurringInvoice}
                            onChange={(content) => handleEmailTemplateChange('recurringInvoice', content)}
                            placeholder="Write your recurring invoice email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.recurringInvoice || defaultTemplates.recurringInvoice}
                                invoice={{
                                  ...previewData,
                                  recurringDetails: {
                                    frequency: 'monthly',
                                    isActive: true
                                  }
                                }}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.recurringInvoiceSubject || defaultTemplates.recurringInvoiceSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('recurringInvoice', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('recurringInvoice')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Recurring Invoice Template'}
                            </Button>
                      </div>
                    </div>
              </Card>
            </TabsContent>

                    {/* Email Settings Section */}
                    <TabsContent value="settings" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-6">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h2 className="text-2xl font-semibold">Email Settings</h2>
                              <p className="text-sm text-muted-foreground mt-1">
                                By default, {appDisplayName} sends emails using our secure, shared email service. You can
                                optionally connect your own SendGrid account so emails come from your domain and you
                                control deliverability and reputation.
                              </p>
                              <p className="text-sm text-muted-foreground mt-2">
                                This is optional but <span className="font-semibold">recommended</span> for
                                higher-volume senders or teams that want full control over their sending identity.{' '}
                                <a
                                  href="https://sendgrid.com"
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary underline underline-offset-2"
                                >
                                  More Info
                                </a>
                              </p>
                            </div>
                            <Button 
                              onClick={handleSaveEmailSettings}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Email Settings'}
                            </Button>
                          </div>
                          
                          <div className="grid gap-6 max-w-xl">
                            <div className="space-y-2">
                              <Label>Email Service</Label>
                              <p className="text-sm text-muted-foreground">
                                Emails are sent via Resend from <code className="text-xs bg-muted px-1 py-0.5 rounded">noreply@invoicer.gg</code> using your company name as the display name.
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="fromEmail">From Email Address</Label>
                              <Input
                                id="fromEmail"
                                name="emailSettings.fromEmail"
                                type="email"
                                value={formData.emailSettings?.fromEmail || ''}
                                onChange={handleInputChange}
                                placeholder="Enter the sender email address"
                              />
                              <p className="text-xs text-muted-foreground">
                                This is the &quot;From&quot; address your customers will see on outgoing emails.
                              </p>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Invoice Template */}
                    <TabsContent value="invoice" className="mt-0">
                      <Card className="p-6 shadow-sm">
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-medium">Reminder Email</h3>
                            <p className="text-sm text-muted-foreground">Template for sending reminders to customers</p>
                          </div>
                          <EmailEditor
                            content={formData.emailSettings?.invoiceTemplate || defaultTemplates.invoiceTemplate}
                            onChange={(content) => handleEmailTemplateChange('invoice', content)}
                            placeholder="Write your reminder email template..."
                            previewContent={
                              <InvoiceEmailPreview
                                template={formData.emailSettings?.invoiceTemplate || defaultTemplates.invoiceTemplate}
                                invoice={previewData}
                                companyName={formData.name || ''}
                              />
                            }
                            companyId={companyId}
                            subject={formData.emailSettings?.invoiceTemplateSubject || defaultTemplates.invoiceTemplateSubject}
                            onSubjectChange={(subject) => handleEmailSubjectChange('invoice', subject)}
                            paymentUrl={"https://www.secured-payment-processing.com/pay/preview"}
                          />
                          <div className="flex justify-end">
                            <Button 
                              onClick={() => handleSaveTemplate('invoice')}
                              disabled={updateSettingsMutation.isPending}
                            >
                              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Reminder Template'}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </Card>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <UsersTabContent companyId={companyId} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

// User tab component
function UsersTabContent({ companyId }: { companyId?: string }) {
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  
  if (!companyId) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="w-full">
      <CardHeader className="border-b pb-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              Manage users and permission roles for your company
            </CardDescription>
          </div>
          <div className="flex space-x-2">
            <Button 
              variant={activeTab === 'users' ? 'default' : 'outline'} 
              onClick={() => setActiveTab('users')}
            >
              <Users className="w-4 h-4 mr-2" />
              Users
            </Button>
            <Button 
              variant={activeTab === 'roles' ? 'default' : 'outline'} 
              onClick={() => setActiveTab('roles')}
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              Roles & Permissions
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {activeTab === 'users' ? (
          <UsersList companyId={companyId} />
        ) : (
          <RolesManagement companyId={companyId} />
        )}
      </CardContent>
    </Card>
  );
}

// Users List Component
function UsersList({ companyId }: { companyId: string }) {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<Record<string, string>>({
    userInvitation: defaultTemplates.userInvitation,
    userInvitationSubject: defaultTemplates.userInvitationSubject
  });

  // Fetch users, roles, and company settings
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [usersData, rolesData, companyData] = await Promise.all([
          getUsers(companyId),
          getRoles(companyId),
          getCompany(companyId)
        ]);
        setUsers(usersData as User[]);
        setRoles(rolesData as Role[]);
        
        // Commented out to avoid the unused variable error
        // setCompanySettings(companyData as CompanySettings);
        
        // Set email templates from company settings or use defaults
        if (companyData?.emailSettings) {
          setEmailTemplates({
            userInvitation: companyData.emailSettings.userInvitation || defaultTemplates.userInvitation,
            userInvitationSubject: companyData.emailSettings.userInvitationSubject || defaultTemplates.userInvitationSubject
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification({
          title: 'Error',
          message: 'Failed to load users, roles, or company settings',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [companyId]);

  const handleDeleteUser = async () => {
    if (!currentUser) return;
    
    setIsDeleting(true);
    setDeleteError('');
    setWarningMessage('');
    
    try {
      // First check if the user can be deleted
      const canDeleteResult = await canDeleteUser(currentUser.uid);
      
      if (!canDeleteResult.canDelete) {
        setDeleteError(canDeleteResult.reason || 'This user cannot be deleted');
        showNotification({
          title: "Cannot Delete User",
          message: canDeleteResult.reason || 'This user cannot be deleted',
          type: "error"
        });
        setIsDeleting(false);
        return;
      }
      
      // Check if the user being deleted is an admin
      const isAdmin = currentUser.role === 'admin' || roles.some(r => 
        r.id === currentUser.roleId && 
        r.name.toLowerCase() === 'admin'
      );
      
      if (isAdmin) {
        // Show a warning about deleting an admin user
        setWarningMessage('You are about to delete an admin user. This action cannot be undone.');
      }
      
      // Attempt to delete the user
      await deleteUser(currentUser.uid);
      
      // If successful, refresh the data and close the dialog
      refreshData();
      setShowDeleteConfirmDialog(false);
      
      showNotification({
        title: "Success",
        message: "User deleted successfully",
        type: "success"
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      
      // Extract the error message
      let errorMessage = 'An error occurred while deleting the user.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        setDeleteError(error.message);
      } else {
        setDeleteError(errorMessage);
      }
      
      showNotification({
        title: "Error",
        message: errorMessage,
        type: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData, companyData] = await Promise.all([
        getUsers(companyId),
        getRoles(companyId),
        getCompany(companyId)
      ]);
      setUsers(usersData as User[]);
      setRoles(rolesData as Role[]);
      
      // Update email templates
      if (companyData?.emailSettings) {
        setEmailTemplates({
          userInvitation: companyData.emailSettings.userInvitation || defaultTemplates.userInvitation,
          userInvitationSubject: companyData.emailSettings.userInvitationSubject || defaultTemplates.userInvitationSubject
        });
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Users</h3>
        <Button onClick={() => setShowAddUserDialog(true)}>Add User</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-r-transparent rounded-full" />
        </div>
      ) : (
        <div className="border rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.uid}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.photoURL ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.photoURL}
                              alt={user.displayName || ''}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-500">
                                {user.displayName ? user.displayName.charAt(0) : '?'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.displayName || 'Unnamed User'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.roleId 
                          ? roles.find(r => r.id === user.roleId)?.name || 'No Role Assigned'
                          : 'No Role Assigned'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-primary mr-2"
                        onClick={() => {
                          setCurrentUser(user);
                          setShowEditUserDialog(true);
                        }}
                      >
                        Edit
                      </Button>
                      <DeleteUserButton 
                        user={user} 
                        setCurrentUser={setCurrentUser}
                        setShowDeleteConfirmDialog={setShowDeleteConfirmDialog}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Dialog */}
      <UserDialog
        open={showAddUserDialog}
        onOpenChange={setShowAddUserDialog}
        roles={roles}
        onSuccess={refreshData}
        emailTemplates={emailTemplates}
      />

      {/* Edit User Dialog */}
      <UserDialog
        open={showEditUserDialog}
        onOpenChange={setShowEditUserDialog}
        user={currentUser}
        roles={roles}
        onSuccess={refreshData}
        emailTemplates={emailTemplates}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={showDeleteConfirmDialog}
        onOpenChange={setShowDeleteConfirmDialog}
        title="Delete User"
        description={`Are you sure you want to delete ${currentUser?.displayName || 'this user'}?`}
        onConfirm={handleDeleteUser}
      />
    </div>
  );
}

// Component for the delete button
interface DeleteUserButtonProps {
  user: User;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
  setShowDeleteConfirmDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

function DeleteUserButton({ user, setCurrentUser, setShowDeleteConfirmDialog }: DeleteUserButtonProps): JSX.Element {
  const { canDelete, isChecking, error } = useIsReadyToDelete(user.uid);
  
  return (
    <Button 
      variant="ghost" 
      size="sm"
      className="text-destructive"
      disabled={!canDelete || isChecking}
      title={error || (canDelete ? 'Delete user' : 'Cannot delete user')}
      onClick={() => {
        setCurrentUser(user);
        setShowDeleteConfirmDialog(true);
      }}
    >
      {isChecking ? 'Checking...' : 'Delete'}
    </Button>
  );
}

// Roles Management Component
function RolesManagement({ companyId }: { companyId: string }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const { user: currentUser } = useAuth();


  // Function to ensure an admin role exists with full permissions
  const ensureAdminRoleExists = async (existingRoles: Role[]) => {
    // Check if we have an admin role
    const adminRole = existingRoles.find(role => 
      role.name.toLowerCase() === 'admin' || role.name.toLowerCase() === 'administrator'
    );
    
    if (!adminRole && currentUser) {
      try {
        
        // Create global Admin role with full permissions
        await createRole({
          name: 'Admin',
          description: 'Global administrator role with full access to all features. This role cannot be modified.',
          companyId,
          permissions: {
            dashboard: true,
            invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
            customers: { canView: true, canCreate: true, canEdit: true, canDelete: true },
            products: { canView: true, canCreate: true, canEdit: true, canDelete: true },
            transactions: { canView: true, canCreate: true, canEdit: true, canDelete: true },
            users: { canView: true, canCreate: true, canEdit: true, canDelete: true },
            settings: true,
            virtualTerminal: true,
            donations: { canView: true, canCreate: true, canEdit: true, canDelete: true }
          },
          isDefault: true,
          isSystemRole: true, // Mark as a system role that cannot be modified
          createdBy: currentUser.uid
        });
        
        // Refresh roles to show the new admin role
        await refreshRoles();
      } catch (error) {
        console.error('Error creating admin role:', error);
      }
    }
  };

  // Fetch roles
  useEffect(() => {
    async function fetchRoles() {
      try {
        setLoading(true);
        const rolesData = await getRoles(companyId);
        setRoles(rolesData as Role[]);
        
        // Ensure admin role exists
        await ensureAdminRoleExists(rolesData as Role[]);
      } catch (error) {
        console.error('Error fetching roles:', error);
        showNotification({
          title: 'Error',
          message: 'Failed to load roles',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    }

    fetchRoles();
  }, [companyId]);

  const handleDeleteRole = async () => {
    if (!currentRole) return;
    
    // Prevent deletion of Admin role or any system role
    const isAdminRole = currentRole.name.toLowerCase() === 'admin' || currentRole.name.toLowerCase() === 'administrator';
    const isSystemRole = currentRole.isSystemRole || isAdminRole;
    
    if (isSystemRole) {
      showNotification({
        title: 'Error',
        message: 'System roles cannot be deleted',
        type: 'error'
      });
      setShowDeleteConfirmDialog(false);
      return;
    }
    
    try {
      await deleteRole(currentRole.id);
      
      // Update the roles list
      setRoles(prevRoles => prevRoles.filter(role => role.id !== currentRole.id));
      
      showNotification({
        title: 'Success',
        message: 'Role deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting role:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to delete role. Please try again.',
        type: 'error'
      });
    }
  };

  const refreshRoles = async () => {
    try {
      setLoading(true);
      const rolesData = await getRoles(companyId);
      setRoles(rolesData as Role[]);
    } catch (error) {
      console.error('Error refreshing roles:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Roles & Permissions</h3>
        <Button onClick={() => {
          setShowAddRoleDialog(true);
        }}>Add Role</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-r-transparent rounded-full" />
        </div>
      ) : (
        <div className="border rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.length > 0 ? (
                roles.map((role) => {
                  const isAdminRole = role.name.toLowerCase() === 'admin' || role.name.toLowerCase() === 'administrator';
                  const isSystemRole = role.isSystemRole || isAdminRole; // Consider either explicit flag or admin name
                  return (
                    <tr key={role.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {role.name}
                          {isSystemRole && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              System Role
                            </span>
                          )}
                        </div>
                        {role.isDefault && !isSystemRole && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            Default
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{role.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* This would show the count of users with this role - to be implemented */}
                        <div className="text-sm text-gray-900">-</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!isSystemRole ? (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-primary mr-2"
                              onClick={() => {
                                setCurrentRole(role);
                                setShowEditRoleDialog(true);
                              }}
                            >
                              Edit
                            </Button>
                            {!role.isDefault && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive"
                                onClick={() => {
                                  setCurrentRole(role);
                                  setShowDeleteConfirmDialog(true);
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-500 italic">System role - cannot be modified</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                    No roles found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Role Dialog */}
      <RoleDialog
        open={showAddRoleDialog}
        onOpenChange={(open) => {
          setShowAddRoleDialog(open);
        }}
        onSuccess={() => {
          refreshRoles();
        }}
      />

      {/* Edit Role Dialog */}
      <RoleDialog
        open={showEditRoleDialog}
        onOpenChange={(open) => {
          setShowEditRoleDialog(open);
        }}
        role={currentRole}
        onSuccess={() => {
          refreshRoles();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={showDeleteConfirmDialog}
        onOpenChange={setShowDeleteConfirmDialog}
        title="Delete Role"
        description={`Are you sure you want to delete the role "${currentRole?.name || ''}"?`}
        onConfirm={handleDeleteRole}
      />
    </div>
  );
}
